<template>
  <!-- CRITICAL: Show minimal UI in popup mode during OAuth -->
  <div v-if="isOAuthPopup" class="oauth-popup-container">
    <div class="oauth-processing">
      <div class="spinner"></div>
      <p>Processing Kite login...</p>
      <p class="status-text">{{ oauthStatusText }}</p>
      <button v-if="showCloseButton" class="close-button" @click="forceClosePopup">
        Close Window
      </button>
    </div>
  </div>

  <div v-else class="investment-vehicles-container">
    <!-- Loading Overlay for Stock Price Fetching -->
    <div v-if="isFetchingStockPrice" class="price-loading-overlay">
      <div class="price-loading-content">
        <div class="loading-spinner">
          <AnLoader :text="loadingText" />
        </div>
        <div class="loading-text">
          <h3></h3>
        </div>
      </div>
    </div>

    <!-- Add Stock Dialog -->
    <AddStockDialog
      v-model="isAddStockDialogOpen"
      :mode="stockDialogMode"
      :preselectedStock="selectedStockData"
      :selectedStock="selectedStockData"
      :stockData="selectedStockData"
      :initialData="selectedStockData"
      @update:mode="stockDialogMode = $event"
    />

    <!-- Add Mutual Fund Dialog -->
    <AddMutualFundDialog
      v-model:modelValue="isAddMutualFundDialogOpen"
      :mode="mutualFundDialogMode"
      :userHoldings="[]"
      :prefilledData="selectedMutualFundData"
      @update:mode="mutualFundDialogMode = $event"
    />

    <!-- Add Other Investments Dialog -->
    <AddOtherInvestsDialog
      v-model="isAddOtherInvestsDialogOpen"
      :mode="otherInvestsDialogMode"
      :userHoldings="[]"
      :prefilledData="selectedOtherInvestsData"
      :initialData="selectedOtherInvestsData"
      @update:mode="otherInvestsDialogMode = $event"
    />

    <!-- Add Fixed Income Dialog -->
    <AddFixedIncomeDialog
      v-model="isAddFixedIncomeDialogOpen"
      :mode="fixedIncomeDialogMode"
      :userHoldings="[]"
      :prefilledData="selectedFixedIncomeData"
      :initialData="selectedFixedIncomeData"
      @update:mode="fixedIncomeDialogMode = $event"
    />

    <!-- Buy Alert Dialog -->
    <BuyAlertDialog
      v-model="isBuyAlertDialogOpen"
      :investmentData="buyAlertData"
      @contact-team="handleContactTeam"
    />

    <!-- Buy Confirmation Dialog (Zerodha) -->
    <BuyConfirmationDialog
      v-model="isBuyConfirmDialogOpen"
      :stockData="buyConfirmStockData"
      @confirm="handleBuyConfirm"
      @cancel="handleBuyCancel"
    />

    <!-- START: Bulk Add Confirmation Dialog -->
    <Dialog
      v-model:visible="isBulkAddConfirmDialogOpen"
      modal
      :style="{ width: '600px' }"
      :closable="!isBulkProcessing"
      :dismissableMask="!isBulkProcessing"
      header="Confirm Portfolio Investment"
      class="bulk-confirmation-dialog"
    >
      <div class="bulk-confirm-content">
        <div class="investment-summary">
          <h3>Investment Summary</h3>
          <p class="total-amount">Total Investment: <strong>₹10,00,000</strong></p>
          <p class="asset-count">{{ processedAssetClasses.length }} Asset Classes</p>
        </div>

        <div class="allocation-breakdown">
          <h4>Allocation Breakdown:</h4>
          <div class="breakdown-list">
            <div
              v-for="assetClass in processedAssetClasses"
              :key="assetClass.originalClass"
              class="breakdown-item"
            >
              <div class="asset-info">
                <span class="asset-name">{{ assetClass.name }}</span>
                <span class="asset-type">{{ assetClass.originalClass }}</span>
              </div>
              <div class="allocation-info">
                <span class="percentage">{{ assetClass.percentage }}%</span>
                <span class="amount">₹{{ formatAllocationAmount(assetClass.percentage) }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="processing-status" v-if="isBulkProcessing">
          <div class="progress-info">
            <h4>Processing Investments...</h4>
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{ width: bulkProcessingProgress + '%' }"
              ></div>
            </div>
            <p class="progress-text">
              {{ bulkProcessingCurrentStep }}
              ({{ bulkProcessingCompletedCount }}/{{ totalVehiclesCount }} vehicles)
            </p>
          </div>

          <div class="processing-results" v-if="bulkProcessingResults.length > 0">
            <h5>Results:</h5>
            <div class="results-list">
              <div
                v-for="result in bulkProcessingResults"
                :key="result.assetClass"
                class="result-item"
                :class="{ success: result.success, error: !result.success }"
              >
                <i :class="result.success ? 'pi pi-check-circle' : 'pi pi-times-circle'"></i>
                <span class="result-text">{{ result.message }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="warning-notice" v-if="!isBulkProcessing">
          <i class="pi pi-exclamation-triangle"></i>
          <p>This will add all investments to your portfolio based on the model allocation. Current market prices will be used for calculations.</p>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <ANButton
            v-if="!isBulkProcessing"
            label="Cancel"
            variant="secondary"
            @click="closeBulkAddConfirmDialog"
          />
          <ANButton
            v-if="bulkProcessingProgress < 100"
            :label="isBulkProcessing ? 'Processing...' : 'Confirm & Invest'"
            variant="primary"
            @click="processBulkAddToPortfolio"
            :loading="isBulkProcessing"
            :disabled="isBulkProcessing"
          />
          <ANButton
            v-if="bulkProcessingProgress >= 100 && !isBulkProcessing"
            label="Close & View Portfolio"
            variant="primary"
            @click="closeBulkAddConfirmDialog(); goBack()"
          />
        </div>
      </template>
    </Dialog>
    <!-- END: Bulk Add Confirmation Dialog -->

    <!-- Page Header (Sticky) -->
    <div class="page-header">
      <div class="header-top">
        <div class="header-left">
          <button class="back-button" @click="goBack">
            ← Back to Portfolio
          </button>

        </div>
        <div class="view-toggle">
          <button
            :class="['toggle-btn', { active: showShortTermReturns }]"
            @click="showShortTermReturns = !showShortTermReturns"
            title="Toggle short-term returns visibility"
          >
            <i class="pi pi-eye"></i>
            {{ showShortTermReturns ? 'Hide Short-Term Returns' : 'Show Short-Term Returns' }}
          </button>
          <div class="view-divider"></div>
          <button
            :class="['toggle-btn', { active: viewMode === 'cards' }]"
            @click="viewMode = 'cards'"
          >
            <i class="pi pi-th-large"></i> Cards
          </button>
          <button
            :class="['toggle-btn', { active: viewMode === 'table' }]"
            @click="viewMode = 'table'"
          >
            <i class="pi pi-list"></i> Table
          </button>
        </div>
      </div>

      <div class="header-filters">
        <div class="filter-chips">
          <button
            :class="['filter-chip', { active: selectedFilter === 'All' }]"
            @click="selectedFilter = 'All'"
          >
            All
          </button>
          <button
            :class="['filter-chip', { active: selectedFilter === 'Equity' }]"
            @click="selectedFilter = 'Equity'"
          >
            Equity
          </button>
          <button
            :class="['filter-chip', { active: selectedFilter === 'Debt' }]"
            @click="selectedFilter = 'Debt'"
          >
            Debt
          </button>
          <button
            :class="['filter-chip', { active: selectedFilter === 'Gold' }]"
            @click="selectedFilter = 'Gold'"
          >
            Gold
          </button>
          <button
            :class="['filter-chip', { active: selectedFilter === 'RE' }]"
            @click="selectedFilter = 'RE'"
          >
            RE
          </button>
          <button
            :class="['filter-chip', { active: selectedFilter === 'MFs' }]"
            @click="selectedFilter = 'MFs'"
          >
            MFs
          </button>
        </div>
        <div class="search-box">
          <i class="pi pi-search"></i>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name or ticker..."
          />
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="loading-state">
      <i class="pi pi-spinner pi-spin"></i>
      <span>Loading investment vehicles...</span>
    </div>

    <!-- Error State -->
    <div v-else-if="!assetClassesFromPortfolio.length" class="error-state">
      <i class="pi pi-exclamation-triangle"></i>
      <h3>No Portfolio Data Found</h3>
      <p>Please complete your risk assessment first to view investment vehicles.</p>
      <ANButton label="Go to Portfolio" variant="primary" @click="goBack" />
    </div>

    <!-- Summary Strip (Sticky) -->
    <div v-else class="summary-strip">
      <div class="summary-content">
        <!-- Integrated Portfolio Overview Card -->
        <div class="portfolio-overview-section">
          <div class="portfolio-overview-card">
            <!-- Accordion Header -->
            <div class="accordion-header" @click="isPortfolioOverviewExpanded = !isPortfolioOverviewExpanded">
              <div class="accordion-title">
                <i class="pi pi-plus-circle"></i>
                <h4>Add Complete Portfolio</h4>
                <span class="accordion-badge">{{ processedAssetClasses.length }} asset classes • ₹10,00,000</span>
              </div>
              <i :class="['pi', isPortfolioOverviewExpanded ? 'pi-chevron-up' : 'pi-chevron-down', 'accordion-icon']"></i>
            </div>

            <!-- Collapsible Content -->
            <div v-show="isPortfolioOverviewExpanded" class="accordion-content">
              <!-- Action Button Section -->
              <div class="portfolio-action-section">
                <ANButton
                  :label="isBulkProcessing ? 'Processing...' : 'Add All to Portfolio'"
                  variant="primary"
                  size="large"
                  :loading="isBulkProcessing"
                  :disabled="isBulkProcessing || !processedAssetClasses.length"
                  @click="openBulkAddConfirmDialog"
                >
                  <template #icon>
                    <i class="pi pi-shopping-cart" v-if="!isBulkProcessing"></i>
                  </template>
                </ANButton>
              </div>

              <!-- Asset Class Allocation Cards -->
              <div class="integrated-asset-classes">
                <div class="allocation-cards">
                  <div
                    v-for="assetClass in processedAssetClasses"
                    :key="assetClass.name"
                    class="allocation-card"
                    :class="getAssetClassCardClass(assetClass.originalClass)"
                  >
                    <div class="allocation-info">
                      <span class="asset-class-name">{{ assetClass.name }}</span>
                      <span class="allocation-percentage">{{ assetClass.percentage }}%</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Portfolio Returns Section -->
              <div class="integrated-returns">
                <!-- Long-term (CAGR) Returns - Always Visible -->
                <div class="returns-section">
                  <h5 class="returns-section-label">Long-term (CAGR):</h5>
                  <div class="returns-chips-row">
                    <div
                      v-for="period in ['1Y', '3Y', '5Y']"
                      :key="period"
                      :class="['return-chip', getReturnClass(portfolioReturns[period])]"
                    >
                      <span class="period">{{ period }}</span>
                      <span class="return">{{ formatReturn(portfolioReturns[period]) }}</span>
                    </div>
                  </div>
                  <!-- Show note for periods with no data -->
                  <div v-if="hasPortfolioDataGaps" class="data-gap-note">
                    <i class="pi pi-info-circle"></i>
                    <span>Some returns unavailable due to insufficient historical data</span>
                  </div>
                </div>

                <!-- Short-term (Absolute) Returns - Conditional -->
                <div v-if="showShortTermReturns" class="returns-section">
                  <h5 class="returns-section-label short-term-warning">
                    Short-term (Abs):
                    <i class="pi pi-info-circle" title="Short-term returns can be volatile and may not reflect long-term performance"></i>
                  </h5>
                  <div class="returns-chips-row">
                    <div
                      v-for="period in ['3M', '6M', 'YTD']"
                      :key="period"
                      :class="['return-chip', getReturnClass(portfolioReturns[period])]"
                    >
                      <span class="period">{{ period }}</span>
                      <span class="return">{{ formatReturn(portfolioReturns[period]) }}</span>
                    </div>
                  </div>
                  <!-- Show note for periods with no data -->
                  <div v-if="hasShortTermDataGaps" class="data-gap-note">
                    <i class="pi pi-info-circle"></i>
                    <span>Some returns unavailable due to insufficient historical data</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Investment Guide Note -->
    <div v-if="!isLoading && assetClassesFromPortfolio.length" class="investment-guide-note">
      <!-- Accordion Header -->
      <div class="guide-accordion-header" @click="isInvestmentGuideExpanded = !isInvestmentGuideExpanded">
        <div class="guide-accordion-title">
          <div class="guide-note-icon-small">
            <i class="pi pi-lightbulb"></i>
          </div>
          <h4>Smart Investment Calculator</h4>
        </div>
        <i :class="['pi', isInvestmentGuideExpanded ? 'pi-chevron-up' : 'pi-chevron-down', 'guide-accordion-icon']"></i>
      </div>

      <!-- Collapsible Content -->
      <div v-show="isInvestmentGuideExpanded" class="guide-accordion-content">
        <div class="guide-note-content">
          <div class="guide-note-text">
            <p class="guide-note-description">
              When you click <strong>"Add to Portfolio"</strong>, we'll automatically calculate and prefill the optimal
              <span class="highlight-text">number of shares/units</span> based on your portfolio allocation
              for a <span class="amount-highlight">₹10,00,000 investment</span>.
            </p>
            <div class="guide-note-features">
              <div class="feature-item">
                <i class="pi pi-check-circle"></i>
                <span>Pro-rata allocation based on your risk profile</span>
              </div>
              <div class="feature-item">
                <i class="pi pi-pencil"></i>
                <span>Fully customizable - edit quantities as needed</span>
              </div>
              <div class="feature-item">
                <i class="pi pi-clock"></i>
                <span>Current market prices fetched in real-time</span>
              </div>
            </div>
            <div class="guide-note-cta">
              <span class="cta-text">Ready to start? Click any "Add to Portfolio" button below!</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div v-if="!isLoading && assetClassesFromPortfolio.length" class="main-content">
      <!-- Cards View -->
      <div v-if="viewMode === 'cards'" class="cards-view">
        <!-- Multi-Vehicle Asset Classes First -->
        <div
          v-for="assetClass in filteredAssetClasses.filter(ac => ac.vehicles.length > 1)"
          :key="assetClass.name"
          class="asset-class-section"
        >
          <!-- Multi-Vehicle Asset Class - Current Detailed Format -->
            <div class="section-header">
              <div class="header-left">
                <h2>{{ assetClass.name }}
                  <span class="weight-pill">{{ assetClass.percentage }}%</span>
                </h2>
                <span :class="['risk-chip', getRiskClass(assetClass.risk)]">
                  {{ assetClass.risk.toUpperCase() }}
                </span>
                <p class="asset-description">{{ assetClass.description }}</p>
              </div>
              <div class="header-right">
                <div class="sleeve-returns">
                  <!-- Long-term (CAGR) Returns - Always Visible -->
                  <div class="sleeve-returns-section">
                    <h5 class="sleeve-returns-label">Long-term (CAGR)</h5>
                    <div class="sleeve-returns-row">
                      <div
                        v-for="period in ['1Y', '3Y', '5Y']"
                        :key="period"
                        :class="['sleeve-return-chip', getReturnClass(assetClass.sleeveReturns[period])]"
                      >
                        <span class="period">{{ period }}</span>
                        <span class="return">{{ formatReturn(assetClass.sleeveReturns[period]) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Short-term (Absolute) Returns - Conditional -->
                  <div v-if="showShortTermReturns" class="sleeve-returns-section">
                    <h5 class="sleeve-returns-label short-term-warning">
                      Short-term (Abs)
                      <i class="pi pi-info-circle" title="Short-term returns can be volatile and may not reflect long-term performance"></i>
                    </h5>
                    <div class="sleeve-returns-row">
                      <div
                        v-for="period in ['3M', '6M', 'YTD']"
                        :key="period"
                        :class="['sleeve-return-chip', getReturnClass(assetClass.sleeveReturns[period])]"
                      >
                        <span class="period">{{ period }}</span>
                        <span class="return">{{ formatReturn(assetClass.sleeveReturns[period]) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Vehicle Cards -->
            <div class="vehicles-grid">
              <div
                v-for="vehicle in assetClass.vehicles"
                :key="vehicle.SecurityName"
                class="vehicle-card"
              >
                <div class="vehicle-header">
                  <div class="vehicle-info">
                    <h4>{{ vehicle.SecurityName }}</h4>
                    <div class="price-info">
                      <span class="price">{{ formatCurrency(vehicle.PriceMeta.LatestValue) }}</span>
                      <span class="currency">{{ vehicle.PriceMeta.Currency || 'INR' }}</span>
                    </div>
                    <span class="updated">Updated {{ formatDate(vehicle.AsOfDate || vehicle.AsOfMonth) }}</span>

                    <!-- Suggested Quantity -->
                    <div class="suggested-quantity">
                      <i class="pi pi-calculator"></i>
                      <span class="quantity-label">Suggested:</span>
                      <span class="quantity-value">{{ calculateSuggestedQuantity(vehicle, assetClass.percentage) }}</span>
                      <i class="pi pi-info-circle info-icon"
                         :title="'Based on ' + assetClass.percentage + '% allocation (₹' + formatAllocationAmount(assetClass.percentage) + '). May change with current market price.'"></i>
                    </div>
                  </div>
                </div>

                <div class="vehicle-returns">
                  <!-- Long-term (CAGR) Returns - Always Visible -->
                  <div class="returns-section">
                    <h5 class="returns-label">Long-Term (CAGR)</h5>
                    <div class="returns-chips-row">
                      <div
                        v-for="period in ['1Y', '3Y', '5Y']"
                        :key="period"
                        :class="['vehicle-return-chip', getReturnClass(vehicle.HistoricalReturns[period])]"
                      >
                        <span class="period">{{ period }}</span>
                        <span class="return">{{ formatReturn(vehicle.HistoricalReturns[period]) }}</span>
                      </div>
                    </div>
                  </div>

                  <!-- Short-term (Absolute) Returns - Conditional -->
                  <div v-if="showShortTermReturns" class="returns-section">
                    <h5 class="returns-label short-term-warning">
                      Short-Term (Absolute)
                      <i class="pi pi-info-circle" title="Short-term returns can be volatile and may not reflect long-term performance"></i>
                    </h5>
                    <div class="returns-chips-row">
                      <div
                        v-for="period in ['3M', '6M', 'YTD']"
                        :key="period"
                        :class="['vehicle-return-chip', getReturnClass(vehicle.HistoricalReturns[period])]"
                      >
                        <span class="period">{{ period }}</span>
                        <span class="return">{{ formatReturn(vehicle.HistoricalReturns[period]) }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="sparkline-container">
                  <h5 class="sparkline-simple-label">Performance Trend (Annualized)</h5>
                  <div class="sparkline-chart-container">
                    <svg width="140" height="60" viewBox="0 0 140 60">
                      <!-- Y-axis max value -->
                      <text x="5" y="12" class="sparkline-axis-text" :fill="getSparklineColor(vehicle.HistoricalReturns['1Y']) || '#6b7280'">
                        {{ formatSparklineMaxValue(vehicle.HistoricalReturns) }}
                      </text>

                      <!-- Main sparkline (shifted right and down for axes) -->
                      <g transform="translate(15, 10)">
                        <polyline
                          :points="generateSparkline(vehicle.HistoricalReturns)"
                          fill="none"
                          :stroke="getSparklineColor(vehicle.HistoricalReturns['1Y'])"
                          stroke-width="2"
                        />
                        <circle
                          :cx="90"
                          :cy="getLastPoint(vehicle.HistoricalReturns)"
                          r="3"
                          :fill="getSparklineColor(vehicle.HistoricalReturns['1Y'])"
                        />
                      </g>

                      <!-- X-axis labels (dynamic based on available data) -->
                      <text x="20" y="55" class="sparkline-axis-text">{{ getSparklineLabels(vehicle.HistoricalReturns).start }}</text>
                      <text x="115" y="55" class="sparkline-axis-text">{{ getSparklineLabels(vehicle.HistoricalReturns).end }}</text>
                    </svg>
                  </div>
                  <div v-if="hasHighAnnualizedValues(vehicle.HistoricalReturns).hasHighValues" class="sparkline-high-values-note">
                    <strong>Note:</strong> Values >90% may result from annualizing short-term absolute returns.
                  </div>
                </div>

                <div class="vehicle-footer">
                  <div class="vehicle-actions">
                    <ANButton
                      label="Add to Portfolio"
                      variant="outline"
                      size="small"
                      @click="openAddStockDialog(vehicle)"
                    />
                    <ANButton
                      :label="['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass) ? 'Buy (Zerodha)' : 'Get Assistance'"
                      variant="primary"
                      size="small"
                      @click="handleBuyButtonClick(vehicle)"
                      :title="['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass)
                        ? 'Buy directly via Zerodha/Kite. Requires Zerodha account & mobile app for 2FA.'
                        : 'Get help from our investment team to purchase this asset.'"
                    />
                  </div>
                  <div class="sebi-disclosure">
                    <i class="pi pi-info-circle"></i>
                    <span>SEBI RIA Disclosure: {{ ['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass) ? 'Direct purchase through Zerodha. We have no vested interest as per SEBI RIA regulations.' : 'Our investment team will assist you with this purchase. No commission-based advice as per SEBI RIA regulations.' }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div v-if="!assetClass.vehicles || assetClass.vehicles.length === 0" class="empty-state">
              <i class="pi pi-inbox"></i>
              <h3>No vehicles available</h3>
              <p>We're working on adding investment options for this asset class.</p>
              <ANButton label="Notify Me" variant="secondary" size="small" />
            </div>
        </div>

        <!-- Single Vehicle Cards Grid (After Multi-Vehicle) -->
        <div v-if="filteredAssetClasses.some(ac => ac.vehicles.length === 1)" class="single-vehicle-sections">
          <div
            v-for="assetClass in filteredAssetClasses.filter(ac => ac.vehicles.length === 1)"
            :key="assetClass.name"
            class="asset-class-section single-vehicle"
          >
            <!-- Single Vehicle Asset Class - Compact Card -->
            <div class="single-vehicle-card">
            <!-- Header Section -->
            <div class="single-vehicle-header">
              <div class="header-main">
                <div class="title-row">
                  <h2>{{ assetClass.name }}</h2>
                  <span class="weight-pill">{{ assetClass.percentage }}%</span>
                  <span class="price">{{ formatCurrency(assetClass.vehicles[0].PriceMeta.LatestValue) }}</span>
                </div>

                <div class="meta-row">
                  <span :class="['risk-chip', getRiskClass(assetClass.risk)]">
                    {{ assetClass.risk }} Risk
                  </span>
                  <span class="security-name">{{ assetClass.vehicles[0].SecurityName }}</span>
                  <span class="updated">{{ formatDate(assetClass.vehicles[0].AsOfDate || assetClass.vehicles[0].AsOfMonth) }}</span>
                </div>

                <p class="asset-description">{{ assetClass.description }}</p>
              </div>
            </div>

            <!-- Content Section - Two Columns -->
            <div class="single-vehicle-content">
              <!-- Left Column: Returns -->
              <div class="returns-column">
                <!-- Long-term Returns -->
                <div class="returns-block">
                  <h5 class="returns-label">Long-Term (CAGR)</h5>
                  <div class="returns-grid">
                    <div
                      v-for="period in ['1Y', '3Y', '5Y']"
                      :key="period"
                      :class="['return-item', getReturnClass(assetClass.vehicles[0].HistoricalReturns[period])]"
                    >
                      <span class="period">{{ period }}</span>
                      <span class="return">{{ formatReturn(assetClass.vehicles[0].HistoricalReturns[period]) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Short-term Returns (Conditional) -->
                <div v-if="showShortTermReturns" class="returns-block">
                  <h5 class="returns-label short-term-warning">
                    Short-Term (Abs)
                    <i class="pi pi-info-circle" title="Short-term returns can be volatile"></i>
                  </h5>
                  <div class="returns-grid">
                    <div
                      v-for="period in ['3M', '6M', 'YTD']"
                      :key="period"
                      :class="['return-item', getReturnClass(assetClass.vehicles[0].HistoricalReturns[period])]"
                    >
                      <span class="period">{{ period }}</span>
                      <span class="return">{{ formatReturn(assetClass.vehicles[0].HistoricalReturns[period]) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Actions moved to left column -->
                <div class="single-vehicle-actions">
                  <ANButton
                    label="Add to Portfolio"
                    variant="outline"
                    size="small"
                    @click="openAddStockDialog(assetClass.vehicles[0])"
                  />
                  <ANButton
                    :label="['Large Cap', 'Mid Cap', 'Small Cap'].includes(assetClass.vehicles[0].AssetClass) ? 'Buy (Zerodha)' : 'Get Assistance'"
                    variant="primary"
                    size="small"
                    @click="handleBuyButtonClick(assetClass.vehicles[0])"
                    :title="['Large Cap', 'Mid Cap', 'Small Cap'].includes(assetClass.vehicles[0].AssetClass)
                      ? 'Buy directly via Zerodha/Kite. Requires Zerodha account & mobile app for 2FA.'
                      : 'Get help from our investment team to purchase this asset.'"
                  />
                </div>
                <div class="sebi-disclosure compact">
                  <i class="pi pi-info-circle"></i>
                  <span>SEBI RIA: {{ ['Large Cap', 'Mid Cap', 'Small Cap'].includes(assetClass.vehicles[0].AssetClass) ? 'Direct purchase via Zerodha. No vested interest.' : 'Investment team assistance. No commission-based advice.' }}</span>
                </div>
              </div>

              <!-- Right Column: Sparkline & Actions -->
              <div class="sparkline-column">
                <div class="sparkline-container">
                  <div class="sparkline-header">
                    <h5 class="sparkline-label">Performance Trend (Annualized)</h5>
                    <div class="sparkline-info" title="All returns converted to annualized basis">
                      <i class="pi pi-info-circle"></i>
                    </div>
                  </div>
                  <div class="sparkline-chart-container">
                    <svg width="140" height="60" viewBox="0 0 140 60">
                      <!-- Y-axis max value -->
                      <text x="5" y="12" class="sparkline-axis-text" :fill="getSparklineColor(assetClass.vehicles[0].HistoricalReturns['1Y']) || '#6b7280'">
                        {{ formatSparklineMaxValue(assetClass.vehicles[0].HistoricalReturns) }}
                      </text>

                      <!-- Main sparkline -->
                      <g transform="translate(15, 10)">
                        <polyline
                          :points="generateSparkline(assetClass.vehicles[0].HistoricalReturns)"
                          fill="none"
                          :stroke="getSparklineColor(assetClass.vehicles[0].HistoricalReturns['1Y'])"
                          stroke-width="2"
                        />
                        <circle
                          :cx="90"
                          :cy="getLastPoint(assetClass.vehicles[0].HistoricalReturns)"
                          r="3"
                          :fill="getSparklineColor(assetClass.vehicles[0].HistoricalReturns['1Y'])"
                        />
                      </g>

                      <!-- X-axis labels -->
                      <text x="20" y="55" class="sparkline-axis-text">{{ getSparklineLabels(assetClass.vehicles[0].HistoricalReturns).start }}</text>
                      <text x="115" y="55" class="sparkline-axis-text">{{ getSparklineLabels(assetClass.vehicles[0].HistoricalReturns).end }}</text>
                    </svg>
                  </div>
                  <div class="sparkline-note">
                    Shows annualized trend. Short-term data converted for comparison.
                    <span v-if="hasHighAnnualizedValues(assetClass.vehicles[0].HistoricalReturns).hasHighValues" class="high-values-warning">
                      <br><strong>Note:</strong> Values >90% may result from annualizing short-term absolute returns.
                    </span>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Table View -->
      <div v-if="viewMode === 'table'" class="table-view">
        <div class="table-container">
          <table class="vehicles-table">
            <thead>
              <tr>
                <th @click="sortBy('SecurityName')" class="sortable">
                  Name
                  <i :class="getSortIcon('SecurityName')"></i>
                </th>
                <th>Asset Class</th>
                <th>Price</th>
                <th v-if="showShortTermReturns" @click="sortBy('3M')" class="sortable">
                  3M
                  <i :class="getSortIcon('3M')"></i>
                </th>
                <th v-if="showShortTermReturns" @click="sortBy('6M')" class="sortable">
                  6M
                  <i :class="getSortIcon('6M')"></i>
                </th>
                <th v-if="showShortTermReturns" @click="sortBy('YTD')" class="sortable">
                  YTD
                  <i :class="getSortIcon('YTD')"></i>
                </th>
                <th @click="sortBy('1Y')" class="sortable">
                  1Y
                  <i :class="getSortIcon('1Y')"></i>
                </th>
                <th @click="sortBy('3Y')" class="sortable">
                  3Y
                  <i :class="getSortIcon('3Y')"></i>
                </th>
                <th @click="sortBy('5Y')" class="sortable">
                  5Y
                  <i :class="getSortIcon('5Y')"></i>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="vehicle in sortedTableData" :key="vehicle.SecurityName">
                <td>
                  <div class="vehicle-name">
                    <strong>{{ vehicle.SecurityName }}</strong>
                    <span class="asset-class">{{ vehicle.AssetClass }}</span>
                  </div>
                </td>
                <td>
                  <span :class="['asset-class-badge', getAssetClassBadge(vehicle.AssetClass)]">
                    {{ vehicle.AssetClass }}
                  </span>
                </td>
                <td>{{ formatCurrency(vehicle.PriceMeta.LatestValue) }}</td>
                <td v-if="showShortTermReturns" :class="getReturnClass(vehicle.HistoricalReturns['3M'])">
                  {{ formatReturn(vehicle.HistoricalReturns['3M']) }}
                </td>
                <td v-if="showShortTermReturns" :class="getReturnClass(vehicle.HistoricalReturns['6M'])">
                  {{ formatReturn(vehicle.HistoricalReturns['6M']) }}
                </td>
                <td v-if="showShortTermReturns" :class="getReturnClass(vehicle.HistoricalReturns['YTD'])">
                  {{ formatReturn(vehicle.HistoricalReturns['YTD']) }}
                </td>
                <td :class="getReturnClass(vehicle.HistoricalReturns['1Y'])">
                  {{ formatReturn(vehicle.HistoricalReturns['1Y']) }}
                </td>
                <td :class="getReturnClass(vehicle.HistoricalReturns['3Y'])">
                  {{ formatReturn(vehicle.HistoricalReturns['3Y']) }}
                </td>
                <td :class="getReturnClass(vehicle.HistoricalReturns['5Y'])">
                  {{ formatReturn(vehicle.HistoricalReturns['5Y']) }}
                </td>
                <td>
                  <div class="table-actions">
                    <ANButton
                      label="Add"
                      variant="outline"
                      size="small"
                      @click="openAddStockDialog(vehicle)"
                    />
                    <ANButton
                      :label="['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass) ? 'Buy' : 'Assist'"
                      variant="primary"
                      size="small"
                      @click="handleBuyButtonClick(vehicle)"
                    />
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue'
import { useUserStore } from '@/store/user-store'
import ANButton from '@/components/common/AnButton.vue'
import AddStockDialog from '@/components/dashboard/AddStockDialog.vue'
import AddMutualFundDialog from '@/components/dashboard/AddMutualFundDialog.vue'
import AddOtherInvestsDialog from '@/components/dashboard/AddOtherInvestsDialog.vue'
import AddFixedIncomeDialog from '@/components/dashboard/AddFixedIncomeDialog.vue'
import BuyAlertDialog from '@/components/dashboard/BuyAlertDialog.vue'
import BuyConfirmationDialog from '@/components/dashboard/BuyConfirmationDialog.vue'
import AnLoader from '@/components/common/AnLoader.vue'
// START: Import Dialog component for bulk confirmation
import Dialog from 'primevue/dialog'
// END: Import Dialog component for bulk confirmation
// Import centralized stock mappings
import { getStockIdFromCompanyName } from '@/lib/stockMappings.js'
// Import centralized mutual fund mappings
import { getMutualFundIdFromName, getFullMutualFundName } from '@/lib/mutualFundMappings.js'

const store = useUserStore()

// Props for section management
const props = defineProps({
  setSection: {
    type: Function,
    default: null
  }
})

// Reactive data
const isLoading = ref(true)
const isOAuthPopup = ref(false) // Track if we're in OAuth popup mode
const oauthStatusText = ref('Initializing...')
const showCloseButton = ref(false)
const viewMode = ref('cards')
const selectedFilter = ref('All')
const searchQuery = ref('')
const sortField = ref('')
const sortOrder = ref('asc')
const vehiclesData = ref([])
const assetClassesFromPortfolio = ref([])
const showShortTermReturns = ref(false) // Toggle for short-term returns

// Accordion state for collapsible sections
const isPortfolioOverviewExpanded = ref(false)
const isInvestmentGuideExpanded = ref(false)

// Add Stock Dialog state
const isAddStockDialogOpen = ref(false)
const isAddMutualFundDialogOpen = ref(false)
const isAddOtherInvestsDialogOpen = ref(false)
const isAddFixedIncomeDialogOpen = ref(false)
const selectedStockData = ref(null)
const selectedMutualFundData = ref(null)
const selectedOtherInvestsData = ref(null)
const selectedFixedIncomeData = ref(null)
const stockDialogMode = ref("BUY")
const mutualFundDialogMode = ref("BUY")
const otherInvestsDialogMode = ref("ADD")
const fixedIncomeDialogMode = ref("ADD")
const isFetchingStockPrice = ref(false)
const loadingText = ref("Fetching Current Price...")

// START: Bulk Add to Portfolio State Variables
const isBulkAddConfirmDialogOpen = ref(false)
const isBulkProcessing = ref(false)
const bulkProcessingProgress = ref(0)
const bulkProcessingCurrentStep = ref('')
const bulkProcessingCompletedCount = ref(0)
const bulkProcessingResults = ref([])
// END: Bulk Add to Portfolio State Variables

// START: Buy Alert Dialog State Variables
const isBuyAlertDialogOpen = ref(false)
const buyAlertData = ref({
  securityName: '',
  assetClass: '',
  allocatedAmount: 0,
  allocationPercentage: '0.0'
})
// END: Buy Alert Dialog State Variables

// START: Buy Confirmation Dialog State Variables (Zerodha)
const isBuyConfirmDialogOpen = ref(false)
const buyConfirmStockData = ref({
  stockName: '',
  stockSymbol: '',
  currentPrice: 0,
  quantity: 0,
  assetClass: '',
  allocatedAmount: 0
})
// END: Buy Confirmation Dialog State Variables

// Return periods for display
const returnPeriods = ['1M', '3M', '6M', 'YTD', '1Y', '2Y', '3Y', '4Y', '5Y']

// Asset class mapping for filtering
const assetClassFilters = {
  'Equity': ['Large Cap', 'Mid Cap', 'Small Cap'],
  'Debt': ['Fixed Deposit', 'Debt Mutual Fund'],
  'Gold': ['Gold'],
  'RE': ['Real Estate'],
  'MFs': ['Flexi-cap Mutual Fund', 'Debt Mutual Fund']
}

// Risk mapping for asset classes
const riskMapping = {
  'Large Cap': 'Low',
  'Mid Cap': 'Medium',
  'Small Cap': 'High',
  'Fixed Deposit': 'Low',
  'Gold': 'Medium',
  'Flexi-cap Mutual Fund': 'Medium',
  'Debt Mutual Fund': 'Low',
  'Real Estate': 'Medium'
}

// Helper function to implement the rebasing strategy
const rebaseAssetClassWeights = (assetClassData, period) => {
  // Create a copy to work with
  const workingData = assetClassData.map(asset => ({
    ...asset,
    rebasedWeight: asset.weight // Start with original weights
  }))

  // Define the hierarchy for rebasing with expanded rules
  const rebaseHierarchy = [
    {
      source: 'Small Cap',
      targets: ['Mid Cap', 'Large Cap'],
      condition: (asset) => asset.name === 'Small Cap' && !asset.hasData
    },
    {
      source: 'Mid Cap',
      targets: ['Large Cap'],
      condition: (asset) => asset.name === 'Mid Cap' && !asset.hasData
    },
    {
      source: 'Debt Mutual Fund',
      targets: ['Flexi-cap Mutual Fund'],
      condition: (asset) => asset.name === 'Debt Mutual Fund' && !asset.hasData
    }
  ]

  // Apply rebasing rules in order
  rebaseHierarchy.forEach(rule => {
    const sourceAsset = workingData.find(rule.condition)
    if (sourceAsset) {
      // Find target assets that have data
      const targetAssets = workingData.filter(asset =>
        rule.targets.includes(asset.name) && asset.hasData
      )

      if (targetAssets.length > 0) {
        // Distribute the source weight equally among targets (or to single target)
        const redistributedWeight = sourceAsset.rebasedWeight / targetAssets.length

        targetAssets.forEach(target => {
          target.rebasedWeight += redistributedWeight
        })

        // Set source weight to 0
        sourceAsset.rebasedWeight = 0

        console.log(`Rebasing ${period}: Redistributed ${sourceAsset.name} weight (${sourceAsset.weight.toFixed(3)}) to:`,
          targetAssets.map(t => `${t.name}: +${redistributedWeight.toFixed(3)}`))
      } else {
        console.warn(`Rebasing ${period}: No valid targets found for ${sourceAsset.name}`)
      }
    }
  })

  return workingData
}

// Computed properties
const filteredAssetClasses = computed(() => {
  return processedAssetClasses.value.filter(assetClass => {
    // Filter by selected filter
    if (selectedFilter.value !== 'All') {
      const filterClasses = assetClassFilters[selectedFilter.value] || []
      if (!filterClasses.some(fc => assetClass.originalClass === fc)) {
        return false
      }
    }

    // Filter by search query
    if (searchQuery.value) {
      const query = searchQuery.value.toLowerCase()
      return assetClass.name.toLowerCase().includes(query) ||
             assetClass.vehicles.some(v => v.SecurityName.toLowerCase().includes(query))
    }

    return true
  })
})

const processedAssetClasses = computed(() => {
  if (!vehiclesData.value.length || !assetClassesFromPortfolio.value.length) return []

  return assetClassesFromPortfolio.value
    .filter(portfolioClass => parseFloat(portfolioClass.percentage) > 0) // Filter out 0% allocations
    .map(portfolioClass => {
      // Find vehicles for this asset class
      const vehicles = vehiclesData.value.filter(v => v.AssetClass === portfolioClass.originalClass)

      // Calculate sleeve returns with proper handling of missing data
      const sleeveReturns = {}
      returnPeriods.forEach(period => {
        const validReturns = vehicles
          .map(v => v.HistoricalReturns[period])
          .filter(r => r !== undefined && r !== null)

        if (validReturns.length > 0) {
          // Equal-weight average of available securities
          const avg = validReturns.reduce((sum, ret) => sum + ret, 0) / validReturns.length
          sleeveReturns[period] = avg
        } else {
          // No data available for this asset class and period
          sleeveReturns[period] = null
        }
      })

      return {
        ...portfolioClass,
        vehicles,
        sleeveReturns,
        risk: riskMapping[portfolioClass.originalClass] || 'Medium'
      }
    })
})

const portfolioReturns = computed(() => {
  const returns = {}
  const debugInfo = {}

  returnPeriods.forEach(period => {
    // Step 1: Identify which asset classes have data for this period
    const assetClassData = processedAssetClasses.value.map(assetClass => ({
      name: assetClass.originalClass,
      displayName: assetClass.name,
      weight: parseFloat(assetClass.percentage) / 100,
      return: assetClass.sleeveReturns[period],
      hasData: assetClass.sleeveReturns[period] !== null && assetClass.sleeveReturns[period] !== undefined
    }))

    // Step 2: Implement rebasing strategy for missing data
    const rebasedData = rebaseAssetClassWeights(assetClassData, period)

    // Step 3: Calculate portfolio return with rebased weights
    let weightedSum = 0
    let totalRebasedWeight = 0
    let missingDataClasses = []

    rebasedData.forEach(asset => {
      if (asset.hasData && asset.rebasedWeight > 0) {
        weightedSum += asset.rebasedWeight * asset.return
        totalRebasedWeight += asset.rebasedWeight
      } else if (!asset.hasData) {
        missingDataClasses.push(asset.displayName)
      }
    })

    // Step 4: Store result and debug info
    if (totalRebasedWeight > 0) {
      returns[period] = weightedSum
      debugInfo[period] = {
        originalWeights: assetClassData.reduce((acc, asset) => ({ ...acc, [asset.name]: asset.weight }), {}),
        rebasedWeights: rebasedData.reduce((acc, asset) => ({ ...acc, [asset.name]: asset.rebasedWeight }), {}),
        missingDataClasses,
        totalRebasedWeight,
        calculatedReturn: weightedSum
      }
    } else {
      returns[period] = null
      debugInfo[period] = {
        error: 'No asset classes have data for this period',
        missingDataClasses: assetClassData.map(a => a.displayName)
      }
    }
  })

  // Log debug information for troubleshooting
  console.log('Portfolio Returns Debug Info:', debugInfo)

  return returns
})

const sortedTableData = computed(() => {
  if (!sortField.value) return vehiclesData.value

  return [...vehiclesData.value].sort((a, b) => {
    let aVal, bVal

    if (sortField.value === 'SecurityName') {
      aVal = a.SecurityName
      bVal = b.SecurityName
    } else {
      aVal = a.HistoricalReturns[sortField.value] || 0
      bVal = b.HistoricalReturns[sortField.value] || 0
    }

    if (typeof aVal === 'string') {
      return sortOrder.value === 'asc'
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal)
    }

    return sortOrder.value === 'asc' ? aVal - bVal : bVal - aVal
  })
})

const latestAsOfDate = computed(() => {
  if (!vehiclesData.value.length) return null

  const dates = vehiclesData.value
    .map(v => v.AsOfDate || v.AsOfMonth)
    .filter(Boolean)
    .sort((a, b) => new Date(b) - new Date(a))

  return dates[0] || null
})

const hasPortfolioDataGaps = computed(() => {
  const longTermPeriods = ['1Y', '3Y', '5Y']
  return longTermPeriods.some(period => portfolioReturns.value[period] === null)
})

const hasShortTermDataGaps = computed(() => {
  const shortTermPeriods = ['3M', '6M', 'YTD']
  return shortTermPeriods.some(period => portfolioReturns.value[period] === null)
})

const totalVehiclesCount = computed(() => {
  if (!processedAssetClasses.value || processedAssetClasses.value.length === 0) {
    return 0
  }
  return processedAssetClasses.value.reduce((sum, assetClass) => {
    return sum + (assetClass.vehicles ? assetClass.vehicles.length : 0)
  }, 0)
})

// Methods
const scrollToTop = () => {
  // Force scroll to top using multiple methods
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
  document.documentElement.scrollTop = 0
  document.body.scrollTop = 0

  // Also try scrolling the main container
  const container = document.querySelector('.investment-vehicles-container')
  if (container) {
    container.scrollTop = 0
  }
}

const loadData = async () => {
  try {
    isLoading.value = true

    // Get asset classes from portfolio (from MyModelPortfolio normalizedAllocations)
    const portfolioData = store.dashboardData?.portfolioTargetAlloc

    if (!portfolioData) {
      store.fireAlert('Error', 'No portfolio data found. Please complete your risk assessment first.')
      return
    }

    // Extract asset classes and create normalized allocations
    assetClassesFromPortfolio.value = portfolioData.map(allocation => ({
      name: getDisplayName(allocation.assetClassName),
      originalClass: allocation.assetClassName,
      percentage: allocation.targetPct.toFixed(1),
      description: getAssetDescription(allocation.assetClassName)
    }))

    const assetClassesList = portfolioData.map(p => p.assetClassName)

    // Call API to get historical returns
    if (store.getModelPortfolioHistReturns) {
      const response = await store.getModelPortfolioHistReturns(assetClassesList)

      vehiclesData.value = response?.modelPortfolioHistReturns || []
    } else {
      store.fireAlert('Error', 'API method not available')
    }

    if (vehiclesData.value.length === 0) {
      // Even if no vehicles data, we can still show the asset class structure
    }

    // Force scroll to top after data is loaded
    await nextTick()
    scrollToTop()

  } catch (error) {
    store.fireAlert('Error', 'Failed to load investment vehicles data: ' + error.message)
  } finally {
    isLoading.value = false
  }
}

const getDisplayName = (assetClass) => {
  const mapping = {
    'Large Cap': 'Large-Cap Equity',
    'Mid Cap': 'Mid-Cap Equity',
    'Small Cap': 'Small-Cap Equity',
    'Fixed Deposit': 'Fixed Deposits',
    'Gold': 'Gold (SGB)',
    'Flexi-cap Mutual Fund': 'Flexi-Cap Mutual Funds',
    'Debt Mutual Fund': 'Debt Mutual Funds',
    'Real Estate': 'Real Estate'
  }
  return mapping[assetClass] || assetClass
}

const getAssetDescription = (assetClass) => {
  const descriptions = {
    'Large Cap': 'Top 100 companies by market cap per SEBI, offering stability and steady growth.',
    'Mid Cap': 'Companies ranked 101-250 by market cap with higher growth potential.',
    'Small Cap': 'Companies below 250 market cap ranking with high growth but higher volatility.',
    'Fixed Deposit': 'Bank FDs offering capital protection with moderate returns.',
    'Gold': 'Sovereign Gold Bonds with exposure to gold prices plus interest.',
    'Flexi-cap Mutual Fund': 'Diversified equity funds with flexible allocation across market caps.',
    'Debt Mutual Fund': 'Fixed income funds investing in bonds and government securities.',
    'Real Estate': 'REITs and real estate investments for property exposure.'
  }
  return descriptions[assetClass] || 'Investment opportunities in this asset class.'
}

const formatReturn = (value) => {
  if (value === undefined || value === null) return '—'
  const percentage = value * 100
  const formatter = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    signDisplay: 'exceptZero'
  })
  return formatter.format(percentage) + '%'
}

const formatCurrency = (value) => {
  if (value === undefined || value === null) return '—'
  const formatter = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2
  })
  return formatter.format(value)
}

const formatDate = (dateStr) => {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const getReturnClass = (value) => {
  if (value === undefined || value === null) return 'neutral'
  return value >= 0 ? 'positive' : 'negative'
}

const getRiskClass = (risk) => {
  return `risk-${risk.toLowerCase()}`
}

const getAssetClassBadge = (assetClass) => {
  const mapping = {
    'Large Cap': 'large-cap',
    'Mid Cap': 'mid-cap',
    'Small Cap': 'small-cap',
    'Fixed Deposit': 'fixed-deposit',
    'Gold': 'gold',
    'Flexi-cap Mutual Fund': 'flexi-cap-mf',
    'Debt Mutual Fund': 'debt-mf',
    'Real Estate': 'real-estate'
  }
  return mapping[assetClass] || 'default'
}

const getAssetClassCardClass = (assetClass) => {
  const mapping = {
    'Large Cap': 'large-cap-card',
    'Mid Cap': 'mid-cap-card',
    'Small Cap': 'small-cap-card',
    'Fixed Deposit': 'fixed-deposit-card',
    'Gold': 'gold-card',
    'Flexi-cap Mutual Fund': 'flexi-cap-mf-card',
    'Debt Mutual Fund': 'debt-mf-card',
    'Real Estate': 'real-estate-card'
  }
  return mapping[assetClass] || 'default-card'
}

const generateSparkline = (returns) => {
  // Define the time periods in reverse chronological order (oldest to newest)
  const periods = ['5Y', '4Y', '3Y', '2Y', '1Y', '6M', '3M', '1M']

  // Convert all returns to annualized basis for fair comparison
  const annualizedValues = periods.map(period => {
    const rawReturn = returns[period]
    if (rawReturn === undefined || rawReturn === null) return null

    // Convert to annualized return for consistent comparison
    let annualizedReturn
    if (period === '5Y' || period === '4Y' || period === '3Y' || period === '2Y' || period === '1Y') {
      // These are already CAGR (annualized)
      annualizedReturn = rawReturn
    } else {
      // Convert short-term absolute returns to annualized equivalent
      const periodMonths = {
        '6M': 6,
        '3M': 3,
        '1M': 1
      }[period]

      if (periodMonths) {
        // Annualize: (1 + return)^(12/months) - 1
        annualizedReturn = Math.pow(1 + rawReturn, 12 / periodMonths) - 1
      } else {
        annualizedReturn = rawReturn // Fallback
      }
    }

    return annualizedReturn
  }).filter(v => v !== null)

  // console.log('Sparkline Debug - Annualized Comparison:', {
  //   periods,
  //   rawReturns: periods.map(p => ({ period: p, raw: returns[p] })),
  //   annualizedReturns: periods.map((p, i) => ({ period: p, annualized: annualizedValues[i] })),
  //   filteredCount: annualizedValues.length
  // })

  // Need at least 2 data points to draw a line
  if (annualizedValues.length < 2) {
    // console.log('Insufficient data for sparkline, showing flat line')
    return '0,15 100,15' // Flat horizontal line in the middle
  }

  // Find min and max values to normalize the chart
  const min = Math.min(...annualizedValues)
  const max = Math.max(...annualizedValues)
  const range = max - min || 1 // Prevent division by zero

  // console.log('Sparkline scaling (annualized):', { min, max, range })

  // Convert each value to SVG coordinates
  const points = annualizedValues.map((value, index) => {
    // X coordinate: spread evenly across 90px width (with 5px padding on each side)
    const x = (index / (annualizedValues.length - 1)) * 90 + 5

    // Y coordinate: scale value to 20px height, flip for SVG (0,0 is top-left)
    // Higher returns appear at the top of the chart
    const y = 25 - ((value - min) / range) * 20

    return `${x},${y}`
  })

  const sparklinePoints = points.join(' ')
  // console.log('Generated sparkline points (annualized):', sparklinePoints)

  return sparklinePoints
}

// Helper function to check if sparkline has high annualized values from short-term data
const hasHighAnnualizedValues = (returns) => {
  const shortTermPeriods = ['6M', '3M', '1M']
  let hasHighValues = false
  let hasAnnualizedShortTerm = false

  // Check if we have short-term data that gets annualized
  shortTermPeriods.forEach(period => {
    const rawReturn = returns[period]
    if (rawReturn !== undefined && rawReturn !== null) {
      hasAnnualizedShortTerm = true
      const periodMonths = { '6M': 6, '3M': 3, '1M': 1 }[period]
      if (periodMonths) {
        const annualizedReturn = Math.pow(1 + rawReturn, 12 / periodMonths) - 1
        if (annualizedReturn > 0.9) { // Greater than 90%
          hasHighValues = true
        }
      }
    }
  })

  return { hasHighValues, hasAnnualizedShortTerm }
}

const getSparklineLabels = (returns) => {
  // Define the time periods in reverse chronological order (oldest to newest)
  const periods = ['5Y', '4Y', '3Y', '2Y', '1Y', '6M', '3M', '1M']

  // Find available periods with data
  const availablePeriods = periods.filter(period =>
    returns[period] !== undefined && returns[period] !== null
  )

  if (availablePeriods.length === 0) {
    return { start: 'N/A', end: 'N/A' }
  }

  // Return the oldest (start) and newest (end) available periods
  const startPeriod = availablePeriods[0] // Oldest with data
  const endPeriod = availablePeriods[availablePeriods.length - 1] // Newest with data

  return { start: startPeriod, end: endPeriod }
}

const getSparklineColor = (oneYearReturn) => {
  if (oneYearReturn === undefined || oneYearReturn === null) return '#6b7280'
  return oneYearReturn >= 0 ? '#10b981' : '#ef4444'
}

const formatSparklineMaxValue = (returns) => {
  // Get annualized values same as sparkline generation
  const periods = ['5Y', '4Y', '3Y', '2Y', '1Y', '6M', '3M', '1M']
  const annualizedValues = periods.map(period => {
    const rawReturn = returns[period]
    if (rawReturn === undefined || rawReturn === null) return null

    if (period === '5Y' || period === '4Y' || period === '3Y' || period === '2Y' || period === '1Y') {
      return rawReturn
    } else {
      const periodMonths = { '6M': 6, '3M': 3, '1M': 1 }[period]
      return periodMonths ? Math.pow(1 + rawReturn, 12 / periodMonths) - 1 : rawReturn
    }
  }).filter(v => v !== null)

  if (annualizedValues.length === 0) return '0%'

  const maxValue = Math.max(...annualizedValues)
  const percentage = maxValue * 100

  // Format with + sign if positive, rounded to 1 decimal
  const formatted = percentage >= 0
    ? `+${percentage.toFixed(1)}%`
    : `${percentage.toFixed(1)}%`

  return formatted
}

const getLastPoint = (returns) => {
  // Get the most recent return data available and convert to annualized
  const rawLatest = returns['1M'] || returns['3M'] || returns['6M'] || returns['1Y'] || 0

  // Determine which period we're using for the latest point
  let latestPeriod = null
  for (const period of ['1M', '3M', '6M', '1Y']) {
    if (returns[period] !== undefined && returns[period] !== null) {
      latestPeriod = period
      break
    }
  }

  // Convert latest to annualized for consistency with sparkline
  let annualizedLatest
  if (latestPeriod && ['1M', '3M', '6M'].includes(latestPeriod)) {
    const periodMonths = { '6M': 6, '3M': 3, '1M': 1 }[latestPeriod]
    annualizedLatest = Math.pow(1 + rawLatest, 12 / periodMonths) - 1
  } else {
    annualizedLatest = rawLatest // Already annualized for 1Y+
  }

  // Same period order and annualization as sparkline generation
  const periods = ['5Y', '4Y', '3Y', '2Y', '1Y', '6M', '3M', '1M']
  const annualizedValues = periods.map(period => {
    const rawReturn = returns[period]
    if (rawReturn === undefined || rawReturn === null) return null

    if (period === '5Y' || period === '4Y' || period === '3Y' || period === '2Y' || period === '1Y') {
      return rawReturn // Already CAGR
    } else {
      const periodMonths = { '6M': 6, '3M': 3, '1M': 1 }[period]
      return periodMonths ? Math.pow(1 + rawReturn, 12 / periodMonths) - 1 : rawReturn
    }
  }).filter(v => v !== null)

  // console.log('Last point calculation (annualized):', {
  //   rawLatest,
  //   latestPeriod,
  //   annualizedLatest,
  //   availableAnnualizedValues: annualizedValues
  // })

  if (annualizedValues.length < 2) return 15 // Middle of the chart

  // Use same scaling as sparkline generation (annualized values)
  const min = Math.min(...annualizedValues)
  const max = Math.max(...annualizedValues)
  const range = max - min || 1

  // Calculate Y position for the latest point (matches sparkline scaling)
  const y = 25 - ((annualizedLatest - min) / range) * 20

  // console.log('Last point Y position:', y, 'for annualized value:', annualizedLatest)

  return y
}

const sortBy = (field) => {
  if (sortField.value === field) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortField.value = field
    sortOrder.value = 'desc'
  }
}

const getSortIcon = (field) => {
  if (sortField.value !== field) return 'pi pi-sort'
  return sortOrder.value === 'asc' ? 'pi pi-sort-up' : 'pi pi-sort-down'
}

const openAddMutualFundDialog = async (vehicle) => {
  try {
    console.log(`Fetching current NAV for mutual fund: ${vehicle.SecurityName}`)

    // Show loading state with mutual fund specific text
    loadingText.value = "Fetching Mutual Fund Current NAV..."
    isFetchingStockPrice.value = true

    // Fetch current NAV
    let currentNAV = vehicle.PriceMeta?.LatestValue || 0
    try {
      // Get full mutual fund name for API call (backend requires full name)
      const fullMutualFundName = getFullMutualFundName(vehicle.SecurityName)
      const apiName = fullMutualFundName || vehicle.SecurityName

      console.log(`Using full name for API call: ${apiName} (from SecurityName: ${vehicle.SecurityName})`)

      const navData = await store.getStocksMFCurrentPrice(apiName, "M")
      currentNAV = navData?.currentNAV || vehicle.PriceMeta?.LatestValue || 0
      console.log('Fetched current NAV:', currentNAV)
    } catch (error) {
      console.warn('Failed to fetch current NAV, using fallback:', error)
      currentNAV = vehicle.PriceMeta?.LatestValue || 0
    } finally {
      // Hide loading state
      isFetchingStockPrice.value = false
    }

    // Calculate allocation and number of units
    const investmentAmount = 1000000 // 10 lakh investment
    const assetClassAllocation = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
    const allocationPercentage = parseFloat(assetClassAllocation?.percentage || 0) / 100
    const allocatedAmount = Math.round(investmentAmount * allocationPercentage * 100) / 100 // Round to 2 decimal places
    const numberOfUnits = currentNAV > 0 ? Math.round((allocatedAmount / currentNAV) * 100) / 100 : 0 // Round to 2 decimal places

    console.log('Calculated mutual fund allocation:', {
      assetClass: vehicle.AssetClass,
      allocationPercentage: allocationPercentage * 100 + '%',
      allocatedAmount,
      currentNAV,
      numberOfUnits
    })

    // Prepare mutual fund data with pre-filled values
    selectedMutualFundData.value = {
      // Core mutual fund identification
      fundName: vehicle.SecurityName,
      mutualFundName: vehicle.SecurityName,
      // Don't set fundId here - let the dialog determine it from the fund name

      // Pre-filled NAV and investment information
      currentNAV: currentNAV,
      nav: currentNAV,
      amountInvested: allocatedAmount,
      numberOfUnits: numberOfUnits,

      // Current date
      transactionDate: new Date().toISOString().slice(0,10),

      // Classification and metadata
      assetClass: vehicle.AssetClass,
      fundType: vehicle.AssetClass === 'Flexi-cap Mutual Fund' ? 'Equity' : 'Debt',
      fundSubType: vehicle.AssetClass,
      currency: vehicle.PriceMeta?.Currency || 'INR',
      lastUpdated: vehicle.AsOfDate || vehicle.AsOfMonth,

      // Allocation context for reference
      allocationPercentage: allocationPercentage * 100,
      allocatedAmount: allocatedAmount,
      investmentAmount: investmentAmount,

      // Original vehicle data for reference
      originalVehicleData: vehicle
    }

    console.log('Opening AddMutualFundDialog with prepared data:', selectedMutualFundData.value)
    mutualFundDialogMode.value = "BUY"
    isAddMutualFundDialogOpen.value = true

  } catch (error) {
    console.error('Error preparing mutual fund dialog data:', error)
    store.fireAlert('Error', 'Failed to prepare mutual fund data: ' + error.message)
  } finally {
    // Ensure loading state is always reset
    isFetchingStockPrice.value = false
  }
}

const openAddFixedIncomeDialog = async (vehicle) => {
  try {
    console.log(`Setting up fixed income dialog for: ${vehicle.SecurityName} (${vehicle.AssetClass})`)

    // Calculate allocation
    const investmentAmount = 1000000 // 10 lakh investment
    const assetClassAllocation = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
    const allocationPercentage = parseFloat(assetClassAllocation?.percentage || 0) / 100
    const allocatedAmount = Math.round(investmentAmount * allocationPercentage * 100) / 100 // Round to 2 decimal places

    console.log('Calculated fixed income allocation:', {
      assetClass: vehicle.AssetClass,
      allocationPercentage: allocationPercentage * 100 + '%',
      allocatedAmount
    })

    // Prepare fixed income data with pre-filled values
    selectedFixedIncomeData.value = {
      // Investment identification and type
      investmentType: 'Long-Term/Tax-Saving FD', // Default to Long-Term/Tax-Saving FD
      instrumentId: '4', // Corresponds to Long-Term/Tax-Saving FD

      // Pre-filled investment information
      amountInvested: allocatedAmount,
      investedAmount: allocatedAmount,

      // Current date
      transactionDate: new Date().toISOString().slice(0,10),
      investedDate: new Date().toISOString().slice(0,10),

      // Default values that user can edit
      interestRatePercent: '', // User will enter
      instrumentIssuer: '', // User will enter

      // Classification and metadata
      assetClass: vehicle.AssetClass,
      currency: vehicle.PriceMeta?.Currency || 'INR',

      // Additional fields that might be expected
      description: vehicle.SecurityName,
      notes: `Portfolio allocation: ${(allocationPercentage * 100).toFixed(1)}%`,

      // Date fields in different formats
      lastUpdated: vehicle.AsOfDate || vehicle.AsOfMonth,

      // Allocation context for reference
      allocationPercentage: allocationPercentage * 100,
      allocatedAmount: allocatedAmount,
      investmentAmount: investmentAmount,

      // Original vehicle data for reference
      originalVehicleData: vehicle
    }

    console.log('Opening AddFixedIncomeDialog with prepared data:', selectedFixedIncomeData.value)
    fixedIncomeDialogMode.value = "ADD"
    isAddFixedIncomeDialogOpen.value = true

  } catch (error) {
    console.error('Error preparing fixed income dialog data:', error)
    store.fireAlert('Error', 'Failed to prepare fixed income data: ' + error.message)
  }
}

const openAddOtherInvestsDialog = async (vehicle) => {
  try {
    console.log(`Setting up other investments dialog for: ${vehicle.SecurityName} (${vehicle.AssetClass})`)

    // Calculate allocation
    const investmentAmount = 1000000 // 10 lakh investment
    const assetClassAllocation = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
    const allocationPercentage = parseFloat(assetClassAllocation?.percentage || 0) / 100
    const allocatedAmount = Math.round(investmentAmount * allocationPercentage * 100) / 100 // Round to 2 decimal places

    console.log('Calculated other investments allocation:', {
      assetClass: vehicle.AssetClass,
      allocationPercentage: allocationPercentage * 100 + '%',
      allocatedAmount
    })

    // Determine investment type based on asset class
    let investmentType = vehicle.AssetClass
    if (vehicle.AssetClass === 'Gold') {
      investmentType = 'Gold'
    } else if (vehicle.AssetClass === 'Real Estate') {
      investmentType = 'Real Estate'
    }

    // Prepare other investments data with pre-filled values
    selectedOtherInvestsData.value = {
      // Investment identification and type
      investmentName: vehicle.SecurityName,
      investmentType: investmentType,
      assetType: investmentType,

      // Pre-filled investment information
      amountInvested: allocatedAmount,
      currentValue: allocatedAmount, // Same as amount invested initially
      investedAmount: allocatedAmount,

      // Current date
      investedDate: new Date().toISOString().slice(0,10),
      purchaseDate: new Date().toISOString().slice(0,10),
      transactionDate: new Date().toISOString().slice(0,10),

      // Classification and metadata
      assetClass: vehicle.AssetClass,
      category: vehicle.AssetClass,
      subCategory: investmentType,
      currency: vehicle.PriceMeta?.Currency || 'INR',

      // Additional fields that might be expected
      description: vehicle.SecurityName,
      notes: `Portfolio allocation: ${(allocationPercentage * 100).toFixed(1)}%`,

      // Date fields in different formats
      lastUpdated: vehicle.AsOfDate || vehicle.AsOfMonth,

      // Allocation context for reference
      allocationPercentage: allocationPercentage * 100,
      allocatedAmount: allocatedAmount,
      investmentAmount: investmentAmount,

      // Original vehicle data for reference
      originalVehicleData: vehicle
    }

    console.log('Opening AddOtherInvestsDialog with prepared data:', selectedOtherInvestsData.value)
    otherInvestsDialogMode.value = "ADD"
    isAddOtherInvestsDialogOpen.value = true

  } catch (error) {
    console.error('Error preparing other investments dialog data:', error)
    store.fireAlert('Error', 'Failed to prepare investment data: ' + error.message)
  }
}

const openAddStockDialog = async (vehicle) => {
  try {
    // Check if this is a mutual fund asset class
    if (['Flexi-cap Mutual Fund', 'Debt Mutual Fund'].includes(vehicle.AssetClass)) {
      await openAddMutualFundDialog(vehicle)
      return
    }

    // Check if this is a Fixed Deposit asset class
    if (['Fixed Deposit'].includes(vehicle.AssetClass)) {
      await openAddFixedIncomeDialog(vehicle)
      return
    }

    // Check if this is a Gold or Real Estate asset class
    if (['Gold', 'Real Estate'].includes(vehicle.AssetClass)) {
      await openAddOtherInvestsDialog(vehicle)
      return
    }

    // For equity asset classes (Large Cap, Mid Cap, Small Cap), fetch current price and calculate shares
    if (['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass)) {

      // Use the SecurityName as the stock symbol/ticker
      const stockSymbol = vehicle.SecurityName

      console.log(`Fetching current data for ${stockSymbol} (${vehicle.AssetClass})`)

      // Show loading state with stock specific text
      loadingText.value = "Fetching Current Stock Price..."
      isFetchingStockPrice.value = true

      // Fetch current stock price
      let currentStockPrice = vehicle.PriceMeta?.LatestValue || 0
      try {
        const priceData = await store.getStocksMFCurrentPrice(stockSymbol, "S")
        currentStockPrice = priceData?.stockCurrentDayPrice || vehicle.PriceMeta?.LatestValue || 0
        console.log('Fetched current stock price:', currentStockPrice)
      } catch (error) {
        console.warn('Failed to fetch current stock price, using fallback:', error)
        currentStockPrice = vehicle.PriceMeta?.LatestValue || 0
      } finally {
        // Hide loading state
        isFetchingStockPrice.value = false
      }

      // Calculate allocation and number of shares
      const investmentAmount = 1000000 // 10 lakh investment
      const assetClassAllocation = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
      const allocationPercentage = parseFloat(assetClassAllocation?.percentage || 0) / 100
      const allocatedAmount = investmentAmount * allocationPercentage
      const numberOfShares = currentStockPrice > 0 ? Math.floor(allocatedAmount / currentStockPrice) : 0

      console.log('Calculated allocation:', {
        assetClass: vehicle.AssetClass,
        allocationPercentage: allocationPercentage * 100 + '%',
        allocatedAmount,
        currentStockPrice,
        numberOfShares
      })

      // Prepare stock data with pre-filled values
      selectedStockData.value = {
        // Core stock identification
        stockSymbol: stockSymbol,
        stockName: vehicle.SecurityName,
        companyName: vehicle.SecurityName,
        ticker: stockSymbol,

        // Pre-filled price information
        currentPrice: currentStockPrice,
        price: currentStockPrice,
        latestPrice: currentStockPrice,
        buyPrice: currentStockPrice, // Same as current price

        // Pre-calculated quantity
        quantity: numberOfShares,

        // Current date
        transactionDate: new Date(),

        // Market data
        fiftyTwoWeekHigh: currentStockPrice * 1.15,
        fiftyTwoWeekLow: currentStockPrice * 0.85,

        // Classification
        assetClass: vehicle.AssetClass,
        sector: vehicle.AssetClass,
        marketCap: vehicle.AssetClass,

        // Additional metadata
        exchange: 'NSE',
        currency: vehicle.PriceMeta?.Currency || 'INR',
        lastUpdated: new Date().toISOString(),

        // Allocation context for reference
        allocationPercentage: allocationPercentage * 100,
        allocatedAmount: allocatedAmount,
        investmentAmount: investmentAmount,

        // Original vehicle data for reference
        originalVehicleData: vehicle
      }

    } else {
      // For non-equity asset classes, use the existing logic
      selectedStockData.value = {
        // Core identification
        stockSymbol: vehicle.SecurityName,
        stockName: vehicle.SecurityName,
        companyName: vehicle.SecurityName,

        // Price information
        currentPrice: vehicle.PriceMeta.LatestValue,
        price: vehicle.PriceMeta.LatestValue,
        latestPrice: vehicle.PriceMeta.LatestValue,

        // Market data with reasonable defaults
        fiftyTwoWeekHigh: vehicle.PriceMeta.LatestValue * 1.15,
        fiftyTwoWeekLow: vehicle.PriceMeta.LatestValue * 0.85,

        // Classification
        assetClass: vehicle.AssetClass,
        sector: vehicle.AssetClass,
        marketCap: 'Large Cap', // Default for non-equity

        // Additional metadata
        exchange: 'NSE',
        currency: vehicle.PriceMeta.Currency || 'INR',
        lastUpdated: vehicle.AsOfDate || vehicle.AsOfMonth,

        // Original data
        originalVehicleData: vehicle
      }
    }

    console.log('Opening AddStockDialog with prepared data:', selectedStockData.value)
    stockDialogMode.value = "BUY"
    isAddStockDialogOpen.value = true

  } catch (error) {
    console.error('Error preparing stock dialog data:', error)
    store.fireAlert('Error', 'Failed to prepare stock data: ' + error.message)
  } finally {
    // Ensure loading state is always reset
    isFetchingStockPrice.value = false
  }
}

const goBack = () => {
  // Use setSection if available (internal navigation), otherwise fall back to store navigation
  if (props.setSection) {

    props.setSection(1) // Navigate back to model portfolio section
  } else {
    store.setTabView('MyModelPortfolio')
  }
}

// Buy Button Handler - Only equities can be bought via Zerodha/KiteConnect
const handleBuyButtonClick = async (vehicle) => {
  console.log(`🛒 Buy button clicked for: ${vehicle.SecurityName}`)

  // ONLY equities (Large Cap, Mid Cap, Small Cap) can be bought via Zerodha
  if (['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass)) {
    try {
      // Show loading state to fetch current price
      loadingText.value = "Fetching Current Stock Price..."
      isFetchingStockPrice.value = true

      // Fetch current stock price
      let currentStockPrice = vehicle.PriceMeta?.LatestValue || 0
      try {
        const priceData = await store.getStocksMFCurrentPrice(vehicle.SecurityName, "S")
        currentStockPrice = priceData?.stockCurrentDayPrice || vehicle.PriceMeta?.LatestValue || 0
        console.log('Fetched current stock price:', currentStockPrice)
      } catch (error) {
        console.warn('Failed to fetch current stock price, using fallback:', error)
        currentStockPrice = vehicle.PriceMeta?.LatestValue || 0
      } finally {
        isFetchingStockPrice.value = false
      }

      // Calculate allocation and suggested quantity
      const investmentAmount = 1000000 // 10 lakh investment
      const assetClassAllocation = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
      const allocationPercentage = parseFloat(assetClassAllocation?.percentage || 0) / 100
      const allocatedAmount = investmentAmount * allocationPercentage
      const suggestedQuantity = currentStockPrice > 0 ? Math.floor(allocatedAmount / currentStockPrice) : 0

      // Prepare stock data for confirmation dialog
      buyConfirmStockData.value = {
        stockName: vehicle.SecurityName,
        stockSymbol: vehicle.SecurityName,
        currentPrice: currentStockPrice,
        quantity: suggestedQuantity,
        assetClass: vehicle.AssetClass,
        allocatedAmount: allocatedAmount,
        isMutualFund: false
      }

      // Wait a tick before showing dialog to ensure state is ready
      await nextTick()

      // Show confirmation dialog BEFORE Kite login
      isBuyConfirmDialogOpen.value = true
    } catch (error) {
      console.error('Error preparing buy confirmation:', error)
      store.fireAlert('Error', 'Failed to prepare order: ' + error.message)
    }
  } else {
    // For ALL non-equity securities (Mutual Funds, Fixed Deposits, Gold, Real Estate),
    // show the contact dialog - these cannot be bought directly via Zerodha
    const assetClassAllocation = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
    const allocationPercentage = parseFloat(assetClassAllocation?.percentage || 0)
    const allocatedAmount = Math.round((1000000 * allocationPercentage) / 100)

    buyAlertData.value = {
      securityName: vehicle.SecurityName,
      assetClass: vehicle.AssetClass,
      allocatedAmount: allocatedAmount,
      allocationPercentage: allocationPercentage.toFixed(1)
    }

    isBuyAlertDialogOpen.value = true
  }
}

// Kite Login Handler
const startKiteLogin = () => {
  // Replace with your API Key
  const apiKey = "090m1z0udqh27jmh"
  //const apiKey = "4n0hgozftnqjpma7"

  // CRITICAL: The redirect URI configured in your Kite app settings
  // This is where Kite will redirect with ?request_token=xxx&action=login&status=success
  const redirectUri = 'http://localhost:5173/'

  // Build login URL with cache-busting timestamp
  const timestamp = Date.now()

  // Kite login URL - skip_session parameter forces fresh login without using cached credentials
  const loginUrl = `https://kite.zerodha.com/connect/login?v=3&api_key=${apiKey}&skip_session=true&t=${timestamp}`

  console.log('🔐 Opening Kite login in popup window...')
  console.log('📍 Configured Redirect URI in Kite app:', redirectUri)
  console.log(' Login URL:', loginUrl)

  // Show info message to user about pre-filled credentials
  store.fireAlert(
    'Info',
    'Opening Kite login. Note: If you see pre-filled User ID from browser autofill, you can clear it to login with a different account.',
    4000
  )

  // Open Kite login in a centered popup window
  const width = 600
  const height = 700
  const left = (window.screen.width - width) / 2
  const top = (window.screen.height - height) / 2

  // Create unique window name with timestamp to prevent caching
  const windowName = `KiteLogin_${timestamp}`

  // Open Kite login page directly
  const popup = window.open(
    loginUrl,
    windowName,
    `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,location=no,status=no`
  )

  if (popup) {
    popup.focus()
    console.log('✅ Kite login popup opened successfully')
  } else {
    console.error('❌ Popup blocked by browser. Please allow popups for this site.')
    store.fireAlert('Error', 'Popup was blocked. Please allow popups for this site and try again.')
  }
}

// Handle buy confirmation from dialog
const handleBuyConfirm = (orderDetails) => {
  console.log('✅ User confirmed buy order:', orderDetails)

  // Only stocks (equities) go through this flow now
  // Get stockId from centralized mapping for stocks
  const stockId = getStockIdFromCompanyName(orderDetails.stockSymbol)
  console.log(`🔍 [Parent] Looking up stockId for ${orderDetails.stockSymbol}: ${stockId || 'NOT FOUND'}`)

  // Stock payload
  const completeOrderDetails = {
    stockSymbol: orderDetails.stockSymbol,
    stockName: orderDetails.stockName,
    stockId: stockId, // Add stockId for stocks
    quantity: orderDetails.quantity,
    currentPrice: orderDetails.currentPrice,
    totalCost: orderDetails.totalCost,
    assetClass: buyConfirmStockData.value.assetClass,
    mobileNumber: store.dashboardData?.phoneNumber || '',
    pan: store.dashboardData?.pan || '',
    email: store.user?.email || '',
    portfolioId: store.dashboardData?.portfolioId || '',
    isMutualFund: false
  }

  console.log('📦 Complete order details to store:', completeOrderDetails)

  // Use localStorage instead of sessionStorage so popup can access it
  localStorage.setItem('pendingKiteOrder', JSON.stringify(completeOrderDetails))

  // Verify it was stored correctly
  const storedOrder = localStorage.getItem('pendingKiteOrder')
  console.log('✅ Verified stored order in localStorage:', storedOrder)

  console.log('Stored pending order in localStorage, opening Kite login...')

  // Trigger Kite login
  startKiteLogin()
}

// Handle buy cancellation from dialog
const handleBuyCancel = () => {
  console.log('❌ User cancelled buy order')
  // Just close the dialog, no further action needed
}

// Handle contact team action from buy alert dialog
const handleContactTeam = () => {
  // Here you can add logic to open contact form, send analytics event, etc.
  console.log('User requested to contact investment team')
  // For now, just show a simple confirmation
  setTimeout(() => {
    store.fireAlert('Info', 'Thank you for your interest! Our investment team will contact you shortly.')
  }, 300)
}

// Force close popup window
const forceClosePopup = () => {
  console.log('🔒 [Manual] User clicked close button')
  try {
    window.close()
  } catch (e) {
    console.error('❌ Manual close failed:', e)
    // If close fails, try to navigate away
    window.location.href = 'about:blank'
  }
}

// START: Bulk Add to Portfolio Functions
const formatAllocationAmount = (percentage) => {
  const amount = (1000000 * parseFloat(percentage)) / 100
  return new Intl.NumberFormat('en-IN').format(Math.round(amount))
}

const calculateSuggestedQuantity = (vehicle, allocationPercentage) => {
  // Get investment amount from store (passed from MyModelPortfolio) or default to ₹10 lakh
  const investmentAmount = store.modelPortfolioInitialInvestment || 1000000
  const allocPct = parseFloat(allocationPercentage) / 100

  // CRITICAL FIX: Find number of vehicles in this asset class to divide allocation equally
  const assetClass = processedAssetClasses.value.find(ac => ac.originalClass === vehicle.AssetClass)
  const numberOfVehicles = assetClass?.vehicles?.length || 1

  // Divide the asset class allocation equally among all vehicles
  const allocatedAmount = (investmentAmount * allocPct) / numberOfVehicles
  const price = vehicle.PriceMeta?.LatestValue || 0

  if (price === 0) return 'N/A'

  // Different calculation based on asset class
  if (['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass)) {
    // For stocks: number of shares (whole numbers)
    const shares = Math.floor(allocatedAmount / price)
    return `${shares} shares`
  } else if (['Flexi-cap Mutual Fund', 'Debt Mutual Fund'].includes(vehicle.AssetClass)) {
    // For mutual funds: number of units (2 decimal places)
    const units = Math.round((allocatedAmount / price) * 100) / 100
    return `${units} units`
  } else if (['Gold'].includes(vehicle.AssetClass)) {
    // For gold: grams (2 decimal places)
    const grams = Math.round((allocatedAmount / price) * 100) / 100
    return `${grams} grams`
  } else if (['Fixed Deposit'].includes(vehicle.AssetClass)) {
    // For FD: amount
    return `₹${new Intl.NumberFormat('en-IN').format(Math.round(allocatedAmount))}`
  } else if (['Real Estate'].includes(vehicle.AssetClass)) {
    // For Real Estate: amount
    return `₹${new Intl.NumberFormat('en-IN').format(Math.round(allocatedAmount))}`
  }

  return 'N/A'
}

const openBulkAddConfirmDialog = () => {
  // Reset processing states
  isBulkProcessing.value = false
  bulkProcessingProgress.value = 0
  bulkProcessingCurrentStep.value = ''
  bulkProcessingCompletedCount.value = 0
  bulkProcessingResults.value = []

  // Open confirmation dialog
  isBulkAddConfirmDialogOpen.value = true
}

const closeBulkAddConfirmDialog = () => {
  // Reset all processing states when closing
  isBulkProcessing.value = false
  bulkProcessingProgress.value = 0
  bulkProcessingCurrentStep.value = ''
  bulkProcessingCompletedCount.value = 0
  bulkProcessingResults.value = []

  // Close the dialog
  isBulkAddConfirmDialogOpen.value = false
}

const processBulkAddToPortfolio = async () => {
  try {
    isBulkProcessing.value = true
    bulkProcessingResults.value = []
    bulkProcessingCompletedCount.value = 0

    // Calculate total vehicles across all asset classes
    const totalVehicles = processedAssetClasses.value.reduce((sum, assetClass) => sum + assetClass.vehicles.length, 0)
    const investmentAmount = 1000000 // ₹10,00,000

    console.log(`Starting bulk portfolio addition for ${totalVehicles} total vehicles across ${processedAssetClasses.value.length} asset classes`)

    let processedCount = 0

    // Process each asset class and all its vehicles
    for (let i = 0; i < processedAssetClasses.value.length; i++) {
      const assetClass = processedAssetClasses.value[i]

      if (!assetClass.vehicles || assetClass.vehicles.length === 0) {
        // No vehicles available for this asset class
        bulkProcessingResults.value.push({
          assetClass: assetClass.name,
          success: false,
          message: `${assetClass.name}: No investment vehicles available`
        })
        processedCount++
        bulkProcessingCompletedCount.value = processedCount
        bulkProcessingProgress.value = (processedCount / totalVehicles) * 100
        continue
      }

      // Calculate allocation per asset class (to be split among vehicles if multiple)
      const allocationPercentage = parseFloat(assetClass.percentage) / 100
      const totalAllocatedAmount = Math.round(investmentAmount * allocationPercentage * 100) / 100

      // If multiple vehicles in asset class, split allocation equally
      const amountPerVehicle = Math.round((totalAllocatedAmount / assetClass.vehicles.length) * 100) / 100

      console.log(`Processing ${assetClass.name} with ${assetClass.vehicles.length} vehicles, ₹${amountPerVehicle} per vehicle`)

      // Process each vehicle in this asset class
      for (let j = 0; j < assetClass.vehicles.length; j++) {
        const vehicle = assetClass.vehicles[j]

        // Update current step
        bulkProcessingCurrentStep.value = `Processing ${vehicle.SecurityName} (${assetClass.name})...`

        try {
          let result = null

          // Process based on asset class type - Pass skipDashboardReload: true for bulk operations
          if (['Large Cap', 'Mid Cap', 'Small Cap'].includes(vehicle.AssetClass)) {
            // STOCK PROCESSING
            result = await processStockInvestment(vehicle, amountPerVehicle, true) // Skip dashboard reload

          } else if (['Flexi-cap Mutual Fund', 'Debt Mutual Fund'].includes(vehicle.AssetClass)) {
            // MUTUAL FUND PROCESSING
            result = await processMutualFundInvestment(vehicle, amountPerVehicle, true) // Skip dashboard reload

          } else if (['Gold', 'Real Estate'].includes(vehicle.AssetClass)) {
            // OTHER INVESTMENTS PROCESSING
            result = await processOtherInvestment(vehicle, amountPerVehicle, true) // Skip dashboard reload

          } else if (['Fixed Deposit'].includes(vehicle.AssetClass)) {
            // FIXED INCOME PROCESSING
            result = await processFixedIncomeInvestment(vehicle, amountPerVehicle, true) // Skip dashboard reload

          } else {
            throw new Error(`Unsupported asset class: ${vehicle.AssetClass}`)
          }

          // Add successful result
          bulkProcessingResults.value.push({
            assetClass: `${vehicle.SecurityName} (${assetClass.name})`,
            success: true,
            message: `${vehicle.SecurityName}: Successfully added ₹${new Intl.NumberFormat('en-IN').format(amountPerVehicle)}${result?.additionalInfo || ''}`
          })

        } catch (error) {
          console.error(`Error processing ${vehicle.SecurityName}:`, error)

          // Add failed result
          bulkProcessingResults.value.push({
            assetClass: `${vehicle.SecurityName} (${assetClass.name})`,
            success: false,
            message: `${vehicle.SecurityName}: Failed - ${error.message}`
          })
        }

        // Update progress after each vehicle
        processedCount++
        bulkProcessingCompletedCount.value = processedCount
        bulkProcessingProgress.value = (processedCount / totalVehicles) * 100

        // Small delay for better UX
        await new Promise(resolve => setTimeout(resolve, 300))
      }
    }

    // Final step
    bulkProcessingCurrentStep.value = 'Finalizing portfolio setup...'


    bulkProcessingCurrentStep.value = 'Portfolio setup completed!'

    // Reset processing state to show final button
    setTimeout(() => {
      isBulkProcessing.value = false
    }, 500)

    // Show completion message and auto-close dialog after a delay
    setTimeout(() => {
      const successCount = bulkProcessingResults.value.filter(r => r.success).length
      const failedCount = bulkProcessingResults.value.filter(r => !r.success).length

      if (failedCount === 0) {
        store.fireAlert('Success', `Portfolio setup completed! Successfully added all ${successCount} investments.`)
      } else {
        store.fireAlert('Partial Success', `Portfolio setup completed with ${successCount} successful and ${failedCount} failed investments. Please check your portfolio for details.`)
      }

      // Auto-close dialog and navigate back after showing the message
      setTimeout(() => {
        closeBulkAddConfirmDialog()
        goBack()
      }, 3000) // Give user time to read the success message
    }, 1000)

  } catch (error) {
    console.error('Bulk processing error:', error)
    isBulkProcessing.value = false // Reset processing state on error

    store.fireAlert('Error', `Bulk processing failed: ${error.message}`)

    // Close dialog and navigate back on error as well
    setTimeout(() => {
      closeBulkAddConfirmDialog()
      goBack()
    }, 3000)
  }
}

const processStockInvestment = async (vehicle, allocatedAmount, skipDashboardReload = false) => {
  // Fetch current stock price
  let currentStockPrice = vehicle.PriceMeta?.LatestValue || 0
  try {
    const priceData = await store.getStocksMFCurrentPrice(vehicle.SecurityName, "S")
    currentStockPrice = priceData?.stockCurrentDayPrice || vehicle.PriceMeta?.LatestValue || 0
  } catch (error) {
    console.warn('Failed to fetch current stock price, using fallback:', error)
    currentStockPrice = vehicle.PriceMeta?.LatestValue || 0
  }

  const numberOfShares = currentStockPrice > 0 ? Math.floor(allocatedAmount / currentStockPrice) : 0

  // Get stock ID using centralized mapping
  const stockId = getStockIdFromCompanyName(vehicle.SecurityName)
  console.log(`Mapping stock symbol ${vehicle.SecurityName} to stock ID:`, stockId)
  if (!stockId) {
    throw new Error(`StockId not found for ${vehicle.SecurityName}. Please add this stock manually.`)
  }

  console.log(`Found stock ID ${stockId} for ${vehicle.SecurityName}`)

  // Prepare stock data for API
  const stockData = {
    stockId: stockId,
    stockSymbol: vehicle.SecurityName,
    stockName: vehicle.SecurityName,
    numberofShares: numberOfShares,
    buyOrSellPrice: currentStockPrice,
    transactionDate: new Date().toISOString().slice(0,10),
    transactionType: 'A',
    currentPrice: currentStockPrice,
  }

  // Call store method to add stock to portfolio with skipDashboardReload parameter
  if (store.addStock) {
    await store.addStock(stockData, skipDashboardReload)
    return { additionalInfo: ` (${numberOfShares} shares @ ₹${currentStockPrice.toFixed(2)})` }
  } else {
    throw new Error('Stock portfolio API not available')
  }
}

const processMutualFundInvestment = async (vehicle, allocatedAmount, skipDashboardReload = false) => {
  // Fetch current NAV
  let currentNAV = vehicle.PriceMeta?.LatestValue || 0
  const fullMutualFundName = getFullMutualFundName(vehicle.SecurityName)
  const apiName = fullMutualFundName || vehicle.SecurityName

  console.log(`Using full name for API call: ${apiName} (from SecurityName: ${vehicle.SecurityName})`)

    try {
    // Get full mutual fund name for API call (backend requires full name)

    const navData = await store.getStocksMFCurrentPrice(apiName, "M")
    currentNAV = navData?.currentNAV || vehicle.PriceMeta?.LatestValue || 0
  } catch (error) {
    console.warn('Failed to fetch current NAV, using fallback:', error)
    currentNAV = vehicle.PriceMeta?.LatestValue || 0
  }

  const numberOfUnits = currentNAV > 0 ? Math.round((allocatedAmount / currentNAV) * 100) / 100 : 0

  // Get mutual fund ID using centralized mapping
  const mutualFundId = getMutualFundIdFromName(vehicle.SecurityName)
  console.log(`Mapping mutual fund ${vehicle.SecurityName} to fund ID:`, mutualFundId)
  if (!mutualFundId) {
    throw new Error(`MutualFundId not found for ${vehicle.SecurityName}. Please add this fund manually.`)
  }

  console.log(`Found mutual fund ID ${mutualFundId} for ${vehicle.SecurityName}`)

  // Prepare mutual fund data for API
  const mutualFundData = {
    mutualFundId: mutualFundId, // Add the required mutualFundId field
    mutualFundName: apiName,
    amountInvested: allocatedAmount,
    numberOfUnits: numberOfUnits,
    currentNAV: currentNAV,
    transactionDate: new Date().toISOString().slice(0,10),
    transactionType: 'A',
  }

  // Call store method to add mutual fund to portfolio with skipDashboardReload parameter
  if (store.addMutualFund) {
    await store.addMutualFund(mutualFundData, skipDashboardReload)
    return { additionalInfo: ` (${numberOfUnits} units @ NAV ₹${currentNAV.toFixed(2)})` }
  } else {
    throw new Error('Mutual Fund portfolio API not available')
  }
}

const processOtherInvestment = async (vehicle, allocatedAmount, skipDashboardReload = false) => {
  const investmentType = vehicle.AssetClass === 'Gold' ? 'Gold' : 'Real Estate'

  // Prepare other investment data for API
  const otherInvestmentData = {
    investmentName: vehicle.SecurityName,
    investmentType: investmentType,
    amountInvested: allocatedAmount,
    currentValue: allocatedAmount,
    investedDate: new Date().toISOString().slice(0,10),
    currency: vehicle.PriceMeta?.Currency || 'INR'
  }

  // Call store method to add other investment to portfolio with skipDashboardReload parameter
  if (store.addOtherInvestments) {
    await store.addOtherInvestments(otherInvestmentData, skipDashboardReload)
    return { additionalInfo: ` (${investmentType})` }
  } else {
    throw new Error('Other Investments portfolio API not available')
  }
}

const processFixedIncomeInvestment = async (vehicle, allocatedAmount, skipDashboardReload = false) => {
  // Prepare fixed income data for API
  const fixedIncomeData = {
    instrumentId: 4, // Corresponds to Long-Term/Tax-Saving FD
    investmentType: 'Tax Saving FD',
    amountInvested: allocatedAmount,
    transactionDate: new Date().toISOString().slice(0,10),
    instrumentIssuer: 'ICICI Bank', // Default issuer
    interestRatePercent: 7.5 // Default rate - user can edit later
  }

  // Call store method to add fixed income to portfolio with skipDashboardReload parameter
  if (store.addFixedIncomeData) {
    await store.addFixedIncomeData(fixedIncomeData, skipDashboardReload)
    return { additionalInfo: ' (Fixed Deposit @ 7.5% interest)' }
  } else {
    throw new Error('Fixed Income portfolio API not available')
  }
}
// END: Bulk Add to Portfolio Functions

// CRITICAL: Check for OAuth callback IMMEDIATELY when script loads (before Vue renders)
// This runs synchronously as the component script loads
const urlParams = new URLSearchParams(window.location.search)
const requestToken = urlParams.get('request_token')
const isPopupWindow = window.opener && window.opener !== window

console.log('🚀 [SCRIPT LOAD] Immediate OAuth check:', {
  requestToken: requestToken ? 'EXISTS' : 'NONE',
  isPopupWindow,
  hasOpener: !!window.opener,
  url: window.location.href
})

// If OAuth callback detected, set the flag immediately
if (requestToken && isPopupWindow) {
  console.log('⚡ [SCRIPT LOAD] OAUTH CALLBACK DETECTED - Setting flag')
  isOAuthPopup.value = true
}

// Lifecycle
onMounted(async () => {
  // CRITICAL: Check for request_token BEFORE any UI rendering
  // Kite redirects to root URL with query params: ?request_token=xxx&action=login&status=success
  const urlParams = new URLSearchParams(window.location.search)
  const requestToken = urlParams.get('request_token')
  const kiteAction = urlParams.get('action')
  const kiteStatus = urlParams.get('status')
  const isPopupWindow = window.opener && window.opener !== window

  console.log('🔍 [onMounted] Checking URL params:', {
    requestToken,
    kiteAction,
    kiteStatus,
    isPopupWindow,
    hasOpener: !!window.opener,
    url: window.location.href,
    pathname: window.location.pathname,
    search: window.location.search,
    fullURL: window.location.toString()
  })

  // Check if this is a Kite OAuth callback
  // The request_token is the most important indicator
  if (requestToken && isPopupWindow) {
    console.log('🔒 [onMounted] ✅ KITE OAUTH CALLBACK DETECTED')
    console.log('🎫 Request Token:', requestToken)
    console.log('📋 Action:', kiteAction)
    console.log('✅ Status:', kiteStatus)

    // Set OAuth popup mode to show minimal UI
    isOAuthPopup.value = true

    // Check if login was successful (status should be 'success')
    if (!kiteStatus || kiteStatus === 'success') {
      oauthStatusText.value = 'Login successful! Exchanging token...'

      try {
        console.log('📤 [onMounted] Popup: Sending request_token to parent window...')
        oauthStatusText.value = 'Sending token to parent window...'

        // CHANGED: Send request_token to parent window instead of calling API from popup
        // The parent window has the order details stored in localStorage
        if (window.opener && !window.opener.closed) {
          console.log('📨 [onMounted] Sending request_token to parent window...')
          window.opener.postMessage({
            type: 'KITE_REQUEST_TOKEN',
            requestToken: requestToken
          }, window.location.origin)
          oauthStatusText.value = 'Success! Closing window...'
        } else {
          console.error('⚠️ [onMounted] Parent window not accessible')
          oauthStatusText.value = 'Authentication successful but parent window not found.'
        }

        // Show close button immediately
        showCloseButton.value = true

        // Try to close automatically after short delay
        setTimeout(() => {
          try {
            console.log('🔒 [onMounted] Attempting to close popup...')
            window.close()

            // If still open after 500ms, update message
            setTimeout(() => {
              if (!window.closed) {
                console.warn('⚠️ Auto-close failed - popup still open')
                oauthStatusText.value = 'Please click the button below to close this window'
              }
            }, 500)
          } catch (e) {
            console.error('❌ Auto-close error:', e)
            oauthStatusText.value = 'Please click the button below to close this window'
          }
        }, 1500)

      } catch (error) {
        console.error("❌ [onMounted] Backend Exchange Failed:", error)
        console.error("Error details:", {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        })
        oauthStatusText.value = 'Authentication failed. Please try again.'
        showCloseButton.value = true

        // Notify parent window about error
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage({
            type: 'KITE_LOGIN_ERROR',
            error: error.response?.data?.message || error.message || 'Token exchange failed'
          }, window.location.origin)
        }
      }
    } else {
      // Kite login was cancelled or failed
      console.warn('⚠️ [onMounted] Kite login not successful. Status:', kiteStatus)
      oauthStatusText.value = 'Login was cancelled or failed.'
      showCloseButton.value = true

      // Notify parent window
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage({
          type: 'KITE_LOGIN_ERROR',
          error: 'Login cancelled or failed'
        }, window.location.origin)
      }
    }

    // STOP here - don't initialize the rest of the app
    return
  }

  console.log('📱 [onMounted] Normal app mode - no Kite callback detected')

  // Normal app initialization (not in popup)
  // Ensure DOM is fully rendered before scrolling
  await nextTick()  // Force scroll to top immediately
  scrollToTop()

  // Additional fallback with a small delay
  setTimeout(() => {
    scrollToTop()
  }, 50)

  // Additional fallback for after data loads
  setTimeout(() => {
    scrollToTop()
  }, 500)

  loadData()

  // Listen for messages from popup window (for parent window)
  window.addEventListener('message', async (event) => {
    // Verify origin for security
    if (event.origin !== window.location.origin) return

    console.log('📨 [Parent] Received message from popup:', event.data)

    if (event.data.type === 'KITE_REQUEST_TOKEN') {
      console.log('✅ [Parent] Received request_token from popup:', event.data.requestToken)

      // Retrieve pending order from localStorage
      const pendingOrderJson = localStorage.getItem('pendingKiteOrder')
      console.log('🔍 [Parent] Raw localStorage data:', pendingOrderJson)

      if (!pendingOrderJson) {
        console.error('❌ [Parent] No pending order found in localStorage')
        store.fireAlert('Error', 'Order details not found. Please try again.')
        return
      }

      let orderDetails = null
      try {
        orderDetails = JSON.parse(pendingOrderJson)
        console.log('📦 [Parent] Retrieved and parsed pending order:', orderDetails)
      } catch (parseError) {
        console.error('❌ [Parent] Failed to parse pending order:', parseError)
        store.fireAlert('Error', 'Failed to read order details. Please try again.')
        return
      }

      // Build payload for stock order (only equities use KiteConnect)
      const stockId = getStockIdFromCompanyName(orderDetails.stockSymbol)
      console.log(`🔍 [Parent] Looking up stockId for ${orderDetails.stockSymbol}: ${stockId || 'NOT FOUND'}`)

      const payload = {
        request_token: event.data.requestToken,
        stockSymbol: orderDetails.stockSymbol,
        stockName: orderDetails.stockName,
        stockId: stockId,
        quantity: orderDetails.quantity,
        price: orderDetails.currentPrice,
        totalCost: orderDetails.totalCost,
        assetClass: orderDetails.assetClass,
        mobileNumber: orderDetails.mobileNumber,
        pan: orderDetails.pan,
        email: orderDetails.email,
        portfolioId: orderDetails.portfolioId,
        isMutualFund: false
      }

      console.log('📤 [Parent] Sending complete payload to backend:', payload)

      try {
        // Send request_token and order details to backend
        const response = await store.orderThroughKiteConnect(payload)

        console.log('✅ [Parent] Backend order successful:', response)

        // Clear pending order from localStorage
        localStorage.removeItem('pendingKiteOrder')

        store.fireAlert('Success', 'Order placed successfully through Kite!')
      } catch (error) {
        console.error('❌ [Parent] Backend order failed:', error)
        store.fireAlert('Error', 'Failed to place order: ' + (error.response?.data?.message || error.message))
      }
    } else if (event.data.type === 'KITE_LOGIN_SUCCESS') {
      console.log('✅ [Parent] Received login success from popup:', event.data)
      store.fireAlert('Success', 'Kite account connected successfully!')
    } else if (event.data.type === 'KITE_LOGIN_ERROR') {
      console.log('❌ [Parent] Received login error from popup:', event.data)
      store.fireAlert('Error', 'Kite login failed: ' + event.data.error)
    }
  })
})
</script>

<style scoped>
/* OAuth Popup Container - Minimal UI */
.oauth-popup-container {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

.oauth-processing {
  text-align: center;
  padding: 40px;
}

.oauth-processing .spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border: 4px solid #f3f4f6;
  border-top: 4px solid #0ea5e9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.oauth-processing p {
  font-size: 1rem;
  color: #64748b;
  margin: 0 0 10px 0;
}

.oauth-processing .status-text {
  font-size: 0.9rem;
  color: #475569;
  font-weight: 500;
  margin: 10px 0;
}

.oauth-processing .close-button {
  margin-top: 20px;
  padding: 10px 24px;
  background: #0ea5e9;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.oauth-processing .close-button:hover {
  background: #0284c7;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.3);
}

.oauth-processing .close-button:active {
  transform: translateY(0);
}

.investment-vehicles-container {
  min-height: 100vh;
  background: #f8fafc;
  --header-height: 152px;

  /* Force scroll to top on mount */
  scroll-behavior: smooth;
  overflow-anchor: none; /* Prevent scroll anchoring */
}

/* Loading Overlay for Stock Price Fetching */
.price-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.98);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

.price-loading-content {
  background: white;
  border-radius: 20px;
  padding: 48px 56px;
  text-align: center;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.12),
              0 8px 16px rgba(0, 0, 0, 0.08);
  max-width: 420px;
  width: 90%;
  margin: 20px;
  border: 2px solid #e5e7eb;
  animation: fadeInScale 0.3s ease-out;
}

@keyframes fadeInScale {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.loading-spinner {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
}

.loading-text h3 {
  margin: 0 0 8px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  line-height: 1.3;
}

.loading-text p {
  margin: 0;
  font-size: 0.85rem;
  color: var(--an-text-secondary);
  line-height: 1.4;
  opacity: 0.8;
}

/* Page Header */
.page-header {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 16px 24px;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.back-button {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.back-button:hover {
  background: #e9ecef;
  transform: translateX(-2px);
}

.page-title {
  font-size: 1.8rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
  flex: 1;
  text-align: center;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
}

.back-button {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 8px 16px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.back-button:hover {
  background: #e9ecef;
  transform: translateX(-2px);
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: var(--an-text-primary);
  margin: 0;
}

.view-toggle {
  display: flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 4px;
}

.toggle-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: none;
  background: transparent;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--an-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.toggle-btn.active {
  background: white;
  color: var(--an-primary);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.view-divider {
  width: 1px;
  height: 20px;
  background: #d1d5db;
  margin: 0 8px;
}

.header-filters {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.filter-chips {
  display: flex;
  gap: 8px;
}

.filter-chip {
  padding: 8px 16px;
  border: 1px solid #d1d5db;
  background: white;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--an-text-secondary);
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-chip.active,
.filter-chip:hover {
  background: var(--an-primary);
  color: white;
  border-color: var(--an-primary);
}

.search-box {
  position: relative;
  display: flex;
  align-items: center;
}

.search-box i {
  position: absolute;
  left: 12px;
  color: var(--an-text-secondary);
  z-index: 1;
}

.search-box input {
  padding: 10px 12px 10px 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  min-width: 250px;
  background: white;
}

.search-box input:focus {
  outline: none;
  border-color: var(--an-primary);
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: var(--an-text-secondary);
}

.loading-state i {
  font-size: 2rem;
  color: var(--an-primary);
}

/* Error State */
.error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 16px;
  color: var(--an-text-secondary);
  text-align: center;
}

.error-state i {
  font-size: 3rem;
  color: #f59e0b;
}

.error-state h3 {
  font-size: 1.5rem;
  color: var(--an-text-primary);
  margin: 0;
}

.error-state p {
  font-size: 1rem;
  margin: 0 0 20px 0;
  max-width: 400px;
}

/* Summary Strip */
.summary-strip {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 20px;
  position: static;
  z-index: 85;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.summary-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 1200px;
  margin: 0 auto;
}

/* START: Integrated Portfolio Overview Card Styles */
.portfolio-overview-section {
  width: 100%;
  display: flex;
  justify-content: center;
}

.portfolio-overview-card {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid #0ea5e9;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 4px 15px rgba(14, 165, 233, 0.1);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Accordion Header for Portfolio Overview */
.accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 8px 0;
  transition: all 0.2s ease;
  user-select: none;
}

.accordion-header:hover {
  opacity: 0.8;
}

.accordion-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.accordion-title i.pi-plus-circle {
  color: #0ea5e9;
  font-size: 1.3rem;
}

.accordion-title h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0;
}

.accordion-badge {
  background: rgba(14, 165, 233, 0.15);
  color: #0369a1;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 600;
}

.accordion-icon {
  color: #0ea5e9;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.accordion-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-top: 12px;
  animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.portfolio-action-section {
  display: flex;
  justify-content: center;
  padding: 8px 0;
}

.portfolio-action-section .an-button {
  min-width: 200px;
}

.portfolio-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.header-main {
  flex: 1;
}

.portfolio-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.portfolio-title i {
  color: #0ea5e9;
  font-size: 1.1rem;
}

.portfolio-meta {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.investment-amount {
  font-size: 1.1rem;
  font-weight: 700;
  color: #059669;
}

.asset-count {
  font-size: 0.9rem;
  font-weight: 600;
  color: #7c3aed;
}

.as-of {
  font-size: 0.8rem;
  color: #64748b;
}

.header-action {
  flex-shrink: 0;
}

.integrated-asset-classes {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.integrated-asset-classes .allocation-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
}

.integrated-asset-classes .allocation-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.8);
  border: 2px solid transparent;
  border-radius: 8px;
  min-width: 100px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.integrated-asset-classes .allocation-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
}

.integrated-returns {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.7);
  border-radius: 8px;
  border: 1px solid rgba(14, 165, 233, 0.2);
}

.returns-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.returns-section-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: #475569;
  margin: 0;
  text-align: center;
}

.returns-section-label.short-term-warning {
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.returns-section-label.short-term-warning i {
  font-size: 0.7rem;
  opacity: 0.8;
}

.returns-chips-row {
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
}

.integrated-returns .return-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 10px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 6px;
  min-width: 50px;
  border: 1px solid transparent;
  transition: all 0.2s ease;
}

.integrated-returns .return-chip.positive {
  background: rgba(236, 253, 245, 0.9);
  border-color: #10b981;
  color: #047857;
}

.integrated-returns .return-chip.negative {
  background: rgba(254, 242, 242, 0.9);
  border-color: #ef4444;
  color: #dc2626;
}

.integrated-returns .return-chip.neutral {
  background: rgba(243, 244, 246, 0.9);
  color: var(--an-text-secondary);
}

.integrated-returns .return-chip .period {
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 2px;
  opacity: 0.8;
}

.integrated-returns .return-chip .return {
  font-size: 0.85rem;
  font-weight: 700;
}

.integrated-returns .data-gap-note {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 4px;
  padding: 6px 10px;
  background: rgba(254, 243, 199, 0.8);
  border: 1px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #d97706;
  text-align: center;
  justify-content: center;
}

.integrated-returns .data-gap-note i {
  font-size: 0.65rem;
}

/* Responsive adjustments for integrated portfolio card */
@media (max-width: 768px) {
  .portfolio-overview-card {
    padding: 14px;
    gap: 10px;
  }

  .portfolio-header {
    flex-direction: column;
    gap: 12px;
    text-align: center;
  }

  .portfolio-title {
    font-size: 1.1rem;
    justify-content: center;
  }

  .portfolio-meta {
    justify-content: center;
    gap: 8px;
    font-size: 0.85rem;
  }

  .header-action {
    width: 100%;
  }

  .header-action .an-button {
    width: 100%;
  }  .integrated-asset-classes .allocation-cards {
    gap: 6px;
  }

  .integrated-asset-classes .allocation-card {
    min-width: 90px;
    padding: 6px 10px;
  }

  .returns-chips-row {
    gap: 4px;
  }

  .integrated-returns .return-chip {
    min-width: 45px;
    padding: 4px 8px;
  }

  .integrated-returns {
    padding: 10px 12px;
    gap: 8px;
  }
}

@media (max-width: 640px) {
  .portfolio-overview-card {
    padding: 12px;
    gap: 8px;
  }

  .portfolio-title {
    font-size: 1rem;
  }

  .portfolio-meta {
    font-size: 0.8rem;
    gap: 6px;
  }

  .investment-amount {
    font-size: 0.95rem;
  }

  .asset-count,
  .as-of {
    font-size: 0.75rem;
  }  .integrated-asset-classes .allocation-card {
    min-width: 80px;
    padding: 4px 8px;
  }

  .asset-class-name {
    font-size: 0.65rem;
  }

  .allocation-percentage {
    font-size: 0.8rem;
  }

  .returns-chips-row {
    gap: 3px;
  }

  .integrated-returns .return-chip {
    min-width: 40px;
    padding: 3px 6px;
  }

  .integrated-returns .return-chip .period {
    font-size: 0.65rem;
  }

  .integrated-returns .return-chip .return {
    font-size: 0.75rem;
  }
}
/* END: Integrated Portfolio Overview Card Styles *//* START: Bulk Confirmation Dialog Styles */
.bulk-confirm-content {
  padding: 24px;
}

.investment-summary {
  margin-bottom: 24px;
  text-align: center;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
}

.investment-summary h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.total-amount {
  font-size: 1.1rem;
  color: #059669;
  margin: 8px 0;
}

.asset-count {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  margin: 4px 0 0 0;
}

.allocation-breakdown {
  margin-bottom: 24px;
}
.allocation-breakdown {
  margin-bottom: 24px;
}

.allocation-breakdown h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.breakdown-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 200px;
  overflow-y: auto;
}

.breakdown-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: #f8fafc;
  border-radius: 6px;
  border-left: 4px solid #0ea5e9;
}

.asset-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.asset-name {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
}

.asset-type {
  font-size: 0.8rem;
  color: var(--an-text-secondary);
}

.allocation-info {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.percentage {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0ea5e9;
}

.amount {
  font-size: 0.85rem;
  color: #059669;
  font-weight: 500;
}

.processing-status {
  margin: 20px 0;
}

.progress-info h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 12px 0;
}

.progress-bar {
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 8px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #0ea5e9, #059669);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  margin: 0;
}

.processing-results {
  margin-top: 16px;
}

.processing-results h5 {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0 0 8px 0;
}

.results-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 150px;
  overflow-y: auto;
}

.result-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
}

.result-item.success {
  background: #ecfdf5;
  color: #047857;
}

.result-item.error {
  background: #fef2f2;
  color: #dc2626;
}

.result-text {
  flex: 1;
}

.warning-notice {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  margin-top: 16px;
}

.warning-notice i {
  color: #d97706;
  margin-top: 2px;
}

.warning-notice p {
  margin: 0;
  font-size: 0.9rem;
  color: #92400e;
  line-height: 1.4;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px 24px;
  margin: 0;
}

/* Dialog Header Styling - Force Bold and Prominent */
.bulk-confirmation-dialog :deep(.p-dialog-header) {
  background: linear-gradient(135deg, #758a9f 0%, #f1f5f9 100%);
  border-bottom: 2px solid #e2e8f0;
  padding: 24px 28px !important;
}

.bulk-confirmation-dialog :deep(.p-dialog-title) {
  font-size: 1.8rem !important;
  font-weight: 900 !important;
  color: #0f172a !important;
  text-transform: none !important;
  letter-spacing: -0.025em !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 1.2 !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif !important;
}

.bulk-confirmation-dialog :deep(.p-dialog-header-icon) {
  color: #64748b;
  font-size: 1.2rem;
}

.bulk-confirmation-dialog :deep(.p-dialog-header-icon):hover {
  color: #475569;
  background: rgba(0, 0, 0, 0.05);
}

.bulk-confirmation-dialog :deep(.p-dialog-content) {
  padding: 0;
}

/* Additional fallback for title styling */
.bulk-confirmation-dialog .p-dialog-title,
.bulk-confirmation-dialog .p-dialog-header .p-dialog-title {
  font-size: 1.8rem !important;
  font-weight: 900 !important;
  color: #0f172a !important;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
}
/* END: Bulk Confirmation Dialog Styles */

/* Asset Class Allocation Cards */
.asset-classes-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.asset-classes-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 800px;
}

.asset-classes-label {
  font-size: 1rem;
  font-weight: 600;
  color: var(--an-text-primary);
  margin: 0;
}

.as-of {
  font-size: 0.75rem;
  color: var(--an-text-muted);
  background: rgba(248, 250, 252, 0.9);
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.allocation-cards {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
  width: 100%;
}

.allocation-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 14px;
  background: #f8fafc;
  border: 2px solid transparent;
  border-radius: 8px;
  min-width: 110px;
  transition: all 0.2s ease;
  cursor: pointer;
}

.allocation-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.allocation-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
}

.asset-class-name {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-primary);
  text-align: center;
  line-height: 1.1;
}

.allocation-percentage {
  font-size: 1rem;
  font-weight: 700;
  color: var(--an-primary);
}

/* Asset Class Specific Colors - More Compact */
.allocation-card.large-cap-card {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  border-color: #3b82f6;
}

.allocation-card.large-cap-card .asset-class-name {
  color: #1e40af;
}

.allocation-card.mid-cap-card {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  border-color: #f59e0b;
}

.allocation-card.mid-cap-card .asset-class-name {
  color: #d97706;
}

.allocation-card.small-cap-card {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  border-color: #ef4444;
}

.allocation-card.small-cap-card .asset-class-name {
  color: #dc2626;
}

.allocation-card.fixed-deposit-card {
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  border-color: #10b981;
}

.allocation-card.fixed-deposit-card .asset-class-name {
  color: #166534;
}

.allocation-card.gold-card {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border-color: #f59e0b;
}

.allocation-card.gold-card .asset-class-name {
  color: #d97706;
}

.allocation-card.flexi-cap-mf-card {
  background: linear-gradient(135deg, #faf5ff 0%, #f3e8ff 100%);
  border-color: #8b5cf6;
}

.allocation-card.flexi-cap-mf-card .asset-class-name {
  color: #7c3aed;
}

.allocation-card.debt-mf-card {
  background: linear-gradient(135deg, #ecfccb 0%, #d9f99d 100%);
  border-color: #65a30d;
}

.allocation-card.debt-mf-card .asset-class-name {
  color: #365314;
}

.allocation-card.real-estate-card {
  background: linear-gradient(135deg, #fef7ff 0%, #fae8ff 100%);
  border-color: #d946ef;
}

.allocation-card.real-estate-card .asset-class-name {
  color: #a21caf;
}

.allocation-card.default-card {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
  border-color: #9ca3af;
}

.allocation-card.default-card .asset-class-name {
  color: var(--an-text-primary);
}

.return-chips {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 600px;
}

.portfolio-returns-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.portfolio-returns-label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  margin: 0;
  text-align: center;
}

.portfolio-returns-label.short-term-warning {
  color: #d97706;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.portfolio-returns-label.short-term-warning i {
  font-size: 0.7rem;
  opacity: 0.8;
}

.portfolio-returns-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.return-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 12px;
  background: #f8fafc;
  border-radius: 6px;
  min-width: 50px;
  border: 1px solid transparent;
}

.return-chip.positive {
  background: #ecfdf5;
  border-color: #10b981;
  color: #047857;
}

.return-chip.negative {
  background: #fef2f2;
  border-color: #ef4444;
  color: #dc2626;
}

.return-chip.neutral {
  background: #f3f4f6;
  color: var(--an-text-secondary);
}

.return-chip .period {
  font-size: 0.8rem;
  font-weight: 500;
  margin-bottom: 4px;
  opacity: 0.8;
}

.return-chip .return {
  font-size: 1rem;
  font-weight: 700;
}

.data-gap-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 6px 10px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 4px;
  font-size: 0.7rem;
  color: #d97706;
  text-align: center;
  justify-content: center;
}

.data-gap-note i {
  font-size: 0.65rem;
}

/* Investment Guide Note */
.investment-guide-note {
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0fdf4 100%);
  border: 2px solid #0ea5e9;
  border-radius: 16px;
  margin: 20px;
  padding: 0;
  box-shadow: 0 8px 25px rgba(14, 165, 233, 0.15);
  position: relative;
  overflow: hidden;
}

.investment-guide-note::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #0ea5e9, #10b981, #8b5cf6);
  animation: shimmer 3s ease-in-out infinite;
}

@keyframes shimmer {
  0%, 100% { opacity: 0.8; }
  50% { opacity: 1; }
}

/* Accordion Header for Investment Guide */
.guide-accordion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  padding: 20px 24px;
  transition: all 0.2s ease;
  user-select: none;
}

.guide-accordion-header:hover {
  background: rgba(14, 165, 233, 0.05);
}

.guide-accordion-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.guide-note-icon-small {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.25);
}

.guide-note-icon-small i {
  font-size: 1.1rem;
  color: white;
}

.guide-accordion-title h4 {
  font-size: 1.2rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0;
}

.guide-accordion-icon {
  color: #0ea5e9;
  font-size: 1.2rem;
  transition: transform 0.3s ease;
  flex-shrink: 0;
}

.guide-accordion-content {
  padding: 0 24px 24px 24px;
  animation: slideDown 0.3s ease-out;
}

.guide-note-content {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  position: relative;
}

.guide-note-icon {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 4px 12px rgba(245, 158, 11, 0.25);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

.guide-note-icon i {
  font-size: 1.5rem;
  color: white;
}

.guide-note-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.guide-note-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #0c4a6e;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.guide-note-title i {
  color: #0ea5e9;
  font-size: 1.2rem;
}

.guide-note-description {
  font-size: 1rem;
  color: #1e40af;
  line-height: 1.6;
  margin: 0;
}

.highlight-text {
  background: linear-gradient(120deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
}

.amount-highlight {
  background: linear-gradient(120deg, #10b981 0%, #059669 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 6px;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.guide-note-features {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  color: #1e40af;
}

.feature-item i {
  color: #10b981;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.feature-item span {
  font-weight: 500;
}

.guide-note-cta {
  background: rgba(14, 165, 233, 0.1);
  border: 1px solid #0ea5e9;
  border-radius: 8px;
  padding: 12px 16px;
  margin-top: 8px;
}

.cta-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: #0c4a6e;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

/* Responsive adjustments for guide note */
@media (max-width: 768px) {
  .investment-guide-note {
    margin: 16px;
  }

  .guide-note-content {
    flex-direction: column;
    gap: 16px;
    padding: 20px;
    text-align: center;
  }

  .guide-note-icon {
    width: 50px;
    height: 50px;
    align-self: center;
  }

  .guide-note-icon i {
    font-size: 1.3rem;
  }

  .guide-note-title {
    font-size: 1.1rem;
    justify-content: center;
  }

  .guide-note-description {
    font-size: 0.9rem;
  }

  .guide-note-features {
    gap: 6px;
  }

  .feature-item {
    justify-content: center;
    font-size: 0.85rem;
  }
}

@media (max-width: 640px) {
  .investment-guide-note {
    margin: 12px;
    border-radius: 12px;
  }

  .guide-note-content {
    padding: 16px;
    gap: 12px;
  }

  .guide-note-title {
    font-size: 1rem;
  }

  .guide-note-description {
    font-size: 0.85rem;
  }

  .highlight-text,
  .amount-highlight {
    padding: 1px 6px;
    font-size: 0.85rem;
  }

  .feature-item {
    font-size: 0.8rem;
  }

  .cta-text {
    font-size: 0.85rem;
  }
}

/* Main Content */
.main-content {
  padding: 20px;
}

/* Cards View */
.cards-view {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Single Vehicle Cards - Responsive Grid */
.single-vehicle-sections {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(480px, 1fr));
  gap: 20px;
  align-items: start;
}

.asset-class-section {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  margin-bottom: 24px;
}

.asset-class-section.single-vehicle {
  height: fit-content;
  margin-bottom: 0;
}

/* Single Vehicle Card Styles - Compact Layout */
.single-vehicle-card {
  padding: 20px;
}

.single-vehicle-header {
  margin-bottom: 16px;
}

.header-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.title-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.title-row h2 {
  font-size: 1.3rem;
  font-weight: 600;
  margin: 0;
  color: var(--an-text-primary);
  flex-shrink: 0;
}

.title-row .weight-pill {
  background: var(--an-primary);
  color: white;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
}

.title-row .price {
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--an-primary);
  margin-left: auto;
  flex-shrink: 0;
}

.meta-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.meta-row .risk-chip {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  flex-shrink: 0;
}

.meta-row .security-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--an-text-primary);
  flex: 1;
  min-width: 0;
}

.meta-row .updated {
  font-size: 0.75rem;
  color: var(--an-text-muted);
  flex-shrink: 0;
}

.asset-description {
  font-size: 0.85rem;
  color: var(--an-text-secondary);
  line-height: 1.4;
  margin: 0;
}

/* Content Section - Two Column Layout */
.single-vehicle-content {
  display: grid;
  grid-template-columns: 1fr 180px;
  gap: 20px;
  align-items: start;
}

/* Responsive adjustments for single vehicle content */
@media (max-width: 768px) {
  .single-vehicle-content {
    grid-template-columns: 1fr !important;
    gap: 16px !important;
  }

  .sparkline-column {
    order: 1;
  }

  .returns-column {
    order: 2;
  }

  .single-vehicle-actions {
    order: 3;
    margin-top: 12px;
  }
}

@media (max-width: 640px) {
  .single-vehicle-content {
    grid-template-columns: 1fr !important;
    gap: 12px !important;
  }

  .single-vehicle-actions {
    flex-direction: column !important;
    gap: 8px !important;
    margin-top: 16px;
  }

  .single-vehicle-actions .an-button,
  .single-vehicle-actions button {
    width: 100% !important;
    min-width: unset !important;
  }
}

.returns-column {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.returns-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.returns-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  margin: 0;
}

.returns-label.short-term-warning {
  color: #d97706;
  display: flex;
  align-items: center;
  gap: 4px;
}

.returns-label.short-term-warning i {
  font-size: 0.65rem;
}

.returns-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}

.return-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 6px;
  background: #f8fafc;
  border-radius: 6px;
  border: 1px solid transparent;
}

.return-item.positive {
  background: #ecfdf5;
  border-color: #10b981;
  color: #047857;
}

.return-item.negative {
  background: #fef2f2;
  border-color: #ef4444;
  color: #dc2626;
}

.return-item.neutral {
  background: #f3f4f6;
  color: var(--an-text-secondary);
}

.return-item .period {
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
  margin-bottom: 2px;
}

.return-item .return {
  font-size: 0.85rem;
  font-weight: 600;
}

/* Sparkline Column */
.sparkline-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sparkline-container {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sparkline-header {
  display: flex;
  align-items: center;
  gap: 4px;
}

.sparkline-label {
  font-size: 0.7rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  margin: 0;
}

.sparkline-info {
  color: var(--an-text-muted);
  cursor: help;
}

.sparkline-info i {
  font-size: 0.6rem;
}

.sparkline-chart-container {
  display: flex;
  justify-content: center;
}

.sparkline-note {
  font-size: 0.6rem;
  color: var(--an-text-muted);
  line-height: 1.3;
  background: #f8fafc;
  padding: 4px 6px;
  border-radius: 3px;
  text-align: center;
}

.high-values-warning {
  color: #d97706;
  font-size: 0.55rem;
}

.sparkline-high-values-note {
  font-size: 0.6rem;
  color: #d97706;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 3px;
  padding: 4px 6px;
  margin-top: 4px;
  text-align: center;
  line-height: 1.2;
}

.sparkline-axis-text {
  font-size: 0.55rem;
  font-weight: 500;
  fill: var(--an-text-muted);
  dominant-baseline: middle;
  text-anchor: start;
}

.single-vehicle-actions {
  display: flex !important;
  flex-direction: row !important;
  gap: 8px;
  justify-content: flex-start !important;
  align-items: center;
  width: 100% !important;
}

.single-vehicle-actions .an-button,
.single-vehicle-actions button {
  width: auto !important;
  min-width: 120px !important;
  max-width: none !important;
  flex: 0 0 auto !important;
}

/* Multi-Vehicle Section Header */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px;
  border-bottom: 1px solid #f3f4f6;
}

.header-left h2 {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
  display: flex;
  align-items: center;
  gap: 12px;
}

.weight-pill {
  background: var(--an-primary);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 600;
}

.risk-chip {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.risk-chip.risk-low {
  background: #dbeafe;
  color: #1e40af;
}

.risk-chip.risk-medium {
  background: #fef3c7;
  color: #d97706;
}

.risk-chip.risk-high {
  background: #fee2e2;
  color: #dc2626;
}

.asset-description {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
  line-height: 1.5;
  margin: 0;
}

.sleeve-returns {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sleeve-returns-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.sleeve-returns-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  margin: 0;
}

.sleeve-returns-label.short-term-warning {
  color: #d97706;
  display: flex;
  align-items: center;
  gap: 4px;
}

.sleeve-returns-label.short-term-warning i {
  font-size: 0.6rem;
  opacity: 0.8;
}

.sleeve-returns-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.sleeve-return-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 6px 8px;
  background: #f8fafc;
  border-radius: 4px;
  min-width: 45px;
}

.sleeve-return-chip.positive {
  background: #ecfdf5;
  color: #047857;
}

.sleeve-return-chip.negative {
  background: #fef2f2;
  color: #dc2626;
}

.sleeve-return-chip .period {
  font-size: 0.7rem;
  font-weight: 500;
  margin-bottom: 2px;
  opacity: 0.8;
}

.sleeve-return-chip .return {
  font-size: 0.8rem;
  font-weight: 600;
}

/* Vehicle Cards */
.vehicles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 20px;
  padding: 24px;
}

.vehicle-card {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  transition: all 0.2s ease;
}

.vehicle-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.vehicle-header {
  margin-bottom: 16px;
}

.vehicle-info h4 {
  font-size: 1rem;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: var(--an-text-primary);
}

.price-info {
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 4px;
}

.price {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--an-text-primary);
}

.currency {
  font-size: 0.9rem;
  color: var(--an-text-secondary);
}

.updated {
  font-size: 0.8rem;
  color: var(--an-text-muted);
}

/* Suggested Quantity */
.suggested-quantity {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 8px 10px;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 1px solid #0ea5e9;
  border-radius: 6px;
  font-size: 0.8rem;
}

.suggested-quantity i.pi-calculator {
  color: #0ea5e9;
  font-size: 0.75rem;
}

.suggested-quantity .quantity-label {
  font-weight: 600;
  color: #0c4a6e;
}

.suggested-quantity .quantity-value {
  font-weight: 700;
  color: #0369a1;
  flex: 1;
}

.suggested-quantity .info-icon {
  color: #64748b;
  font-size: 0.7rem;
  cursor: help;
  margin-left: auto;
}

.suggested-quantity .info-icon:hover {
  color: #475569;
}

.vehicle-returns {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
}

.returns-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.returns-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  margin: 0;
}

.returns-label.short-term-warning {
  color: #d97706;
  display: flex;
  align-items: center;
  gap: 4px;
}

.returns-label.short-term-warning i {
  font-size: 0.6rem;
  opacity: 0.8;
}

.returns-chips-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.vehicle-return-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: white;
  border-radius: 4px;
  font-size: 0.8rem;
}

.vehicle-return-chip.positive {
  background: #ecfdf5;
  color: #047857;
}

.vehicle-return-chip.negative {
  background: #fef2f2;
  color: #dc2626;
}

.vehicle-return-chip .period {
  font-weight: 500;
  opacity: 0.8;
}

.vehicle-return-chip .return {
  font-weight: 600;
}

.sparkline-container {
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.sparkline-simple-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--an-text-secondary);
  margin: 0 0 8px 0;
}

.vehicle-footer {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.vehicle-actions {
  display: flex;
  gap: 8px;
}

.vehicle-actions .an-button {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
}

/* SEBI RIA Disclosure */
.sebi-disclosure {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  font-size: 0.7rem;
  color: #92400e;
  line-height: 1.4;
  margin-top: 8px;
}

.sebi-disclosure.compact {
  padding: 6px 8px;
  font-size: 0.65rem;
  margin-top: 6px;
}

.sebi-disclosure i {
  color: #d97706;
  font-size: 0.7rem;
  margin-top: 1px;
  flex-shrink: 0;
}

.sebi-disclosure.compact i {
  font-size: 0.65rem;
}

.sebi-disclosure span {
  flex: 1;
  font-weight: 500;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--an-text-secondary);
}

.empty-state i {
  font-size: 3rem;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  font-size: 1.2rem;
  margin-bottom: 8px;
  color: var(--an-text-primary);
}

.empty-state p {
  margin-bottom: 20px;
  font-size: 0.9rem;
}

/* Table View */
.table-view {
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.table-container {
  overflow-x: auto;
}

.vehicles-table {
  width: 100%;
  border-collapse: collapse;
}

.vehicles-table th {
  background: #f8fafc;
  padding: 16px 12px;
  text-align: left;
  font-weight: 600;
  color: var(--an-text-primary);
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.vehicles-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.vehicles-table th.sortable:hover {
  background: #f3f4f6;
}

.vehicles-table td {
  padding: 16px 12px;
  border-bottom: 1px solid #f3f4f6;
}

.vehicles-table tr:hover {
  background: #f8fafc;
}

.vehicle-name {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.vehicle-name strong {
  font-weight: 600;
  color: var(--an-text-primary);
}

.vehicle-name .asset-class {
  font-size: 0.8rem;
  color: var(--an-text-secondary);
}

.asset-class-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
}

.asset-class-badge.large-cap {
  background: #dbeafe;
  color: #1e40af;
}

.asset-class-badge.mid-cap {
  background: #fef3c7;
  color: #d97706;
}

.asset-class-badge.small-cap {
  background: #fee2e2;
  color: #dc2626;
}

.asset-class-badge.fixed-deposit,
.asset-class-badge.debt-mf {
  background: #f0fdf4;
  color: #166534;
}

.asset-class-badge.gold {
  background: #fffbeb;
  color: #d97706;
}

.asset-class-badge.flexi-cap-mf {
  background: #faf5ff;
  color: #7c3aed;
}

.asset-class-badge.real-estate {
  background: #ecfccb;
  color: #365314;
}

.table-actions {
  display: flex;
  gap: 8px;
}

.positive {
  color: #047857;
  font-weight: 600;
}

.negative {
  color: #dc2626;
  font-weight: 600;
}

.neutral {
  color: var(--an-text-secondary);
}

/* Responsive Design */
@media (max-width: 768px) {
  .page-header {
    padding: 12px 16px;
  }

  .header-top {
    flex-direction: column;
    gap: 16px;
    align-items: flex-start;
  }

  .header-left {
    flex-direction: column;
    gap: 12px;
    align-items: flex-start;
  }

  .header-filters {
    flex-direction: column;
    gap: 16px;
  }

  .filter-chips {
    flex-wrap: wrap;
  }

  .search-box input {
    min-width: 200px;
  }

  .summary-strip {
    padding: 12px 16px;
  }

  .portfolio-overview-card {
    padding: 20px;
    gap: 16px;
  }

  .portfolio-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
  }

  .portfolio-title {
    font-size: 1.2rem;
    justify-content: center;
  }

  .portfolio-description {
    font-size: 0.9rem;
  }

  .header-action {
    width: 100%;
  }

  .header-action .an-button {
    width: 100%;
  }

  .allocation-cards {
    gap: 8px;
    margin-top: 16px;
  }

  .allocation-card {
    min-width: 100px;
    padding: 8px 12px;
  }

  .asset-class-name {
    font-size: 0.7rem;
  }

  .allocation-percentage {
    font-size: 0.9rem;
  }

  .return-chips {
    gap: 10px;
  }

  .portfolio-returns-row {
    gap: 6px;
  }

  .return-chip {
    min-width: 45px;
    padding: 6px 10px;
  }

  .return-chip .period {
    font-size: 0.7rem;
  }

  .return-chip .return {
    font-size: 0.9rem;
  }

  .main-content {
    padding: 16px;
  }

  /* Cards responsive layout */
  .cards-view {
    grid-template-columns: 1fr;
  }

  .section-header {
    flex-direction: column;
    gap: 16px;
  }

  .sleeve-returns {
    flex-wrap: wrap;
    justify-content: flex-start;
  }

  .vehicles-grid {
    grid-template-columns: 1fr;
    padding: 16px;
  }

  .vehicle-actions {
    flex-direction: column;
  }

  .vehicle-actions .an-button {
    width: 100% !important;
    flex: unset !important;
  }

  /* Single vehicle sections responsive */
  .single-vehicle-sections {
    grid-template-columns: 1fr !important;
  }
}

@media (max-width: 640px) {
  .cards-view {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .single-vehicle-card {
    padding: 16px;
  }

  .title-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .title-row .price {
    margin-left: 0;
    align-self: flex-end;
  }

  .returns-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
  }

  .return-item {
    padding: 6px 4px;
  }

  .return-item .period {
    font-size: 0.65rem;
  }

  .return-item .return {
    font-size: 0.8rem;
  }

  /* Ensure sparkline content stacks properly */
  .sparkline-column {
    align-items: stretch;
  }

  .sparkline-chart-container {
    display: flex;
    justify-content: center;
    margin: 8px 0;
  }

  .sparkline-note {
    text-align: center;
    margin-top: 8px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.5rem;
  }

  .view-toggle {
    width: 100%;
  }

  .toggle-btn {
    flex: 1;
    justify-content: center;
  }

  .summary-strip {
    padding: 8px 12px;
  }

  .allocation-cards {
    gap: 6px;
    margin-top: 12px;
  }

  .allocation-card {
    min-width: 90px;
    padding: 6px 10px;
  }

  .asset-class-name {
    font-size: 0.65rem;
  }

  .allocation-percentage {
    font-size: 0.85rem;
  }

  .return-chips {
    gap: 8px;
  }

  .portfolio-returns-row {
    gap: 4px;
    flex-wrap: wrap;
    justify-content: center;
  }

  .return-chip {
    min-width: 40px;
    padding: 4px 8px;
  }

  .return-chip .period {
    font-size: 0.65rem;
  }

  .return-chip .return {
    font-size: 0.8rem;
  }

  /* Single vehicle mobile adjustments */
  .single-vehicle-card {
    padding: 12px;
  }

  .title-row h2 {
    font-size: 1.1rem;
  }

  .title-row .price {
    font-size: 1.1rem;
  }

  .returns-grid {
    grid-template-columns: repeat(3, 1fr);
    gap: 4px;
  }

  .sparkline-chart-container svg {
    width: 120px;
    height: 50px;
  }
}
</style>
